import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ManageOEMModal from "./components/ManageOEMModal";
import ManageSKUModal from "./components/ManageSKUModal";
import AddOneOffModal from "./components/AddOneOffModal";
import KittingModal from "./components/KittingModule/KittingModal";
const KIT_ID_PREFIX = "OEMKIT";
const DATA_COLLECTION_THEME = {
  light: {
    isDark: false,
    surface: "#ffffff",
    elevatedSurface: "#f8f9fb",
    shell: "#fafafa",
    headerBg: "#f5f5f5",
    toolbarBg: "#ffffff",
    toolbarBorder: "#e5e7eb",
    text: "#111827",
    textMuted: "#6b7280",
    border: "#d1d5db",
    borderMuted: "#e5e7eb",
    tableHeaderBg: "#f7f7f7",
    tableFilterBg: "#ffffff",
    rowDivider: "#eeeeee",
    rowCurrentBg: "#ffffff",
    rowSiblingBg: "#fcfcfc",
    prevNextBg: "#ffe6ef",
    ghostBg: "#f7f7f7",
    itemDivider: "#bfc2c7",
    locationDivider: "#e1e4e8",
    cardBg: "#f9fafb",
    cardBorder: "#e5e7eb",
    inputBg: "#ffffff",
    inputText: "#111827",
    inputBorder: "#cbd5f5",
    tagBg: "#eef2ff",
    tagText: "#1f2937"
  },
  dark: {
    isDark: true,
    surface: "#151821",
    elevatedSurface: "#1c1f2b",
    shell: "#0f1117",
    headerBg: "#1f2330",
    toolbarBg: "#181b25",
    toolbarBorder: "rgba(255,255,255,0.09)",
    text: "#f3f4f6",
    textMuted: "#9ca3af",
    border: "rgba(255,255,255,0.15)",
    borderMuted: "rgba(255,255,255,0.08)",
    tableHeaderBg: "#202333",
    tableFilterBg: "#12141f",
    rowDivider: "rgba(255,255,255,0.1)",
    rowCurrentBg: "rgba(255,255,255,0.02)",
    rowSiblingBg: "rgba(255,255,255,0.04)",
    prevNextBg: "rgba(255, 167, 201, 0.2)",
    ghostBg: "rgba(255,255,255,0.07)",
    itemDivider: "rgba(255,255,255,0.3)",
    locationDivider: "rgba(255,255,255,0.18)",
    cardBg: "#1c1f2b",
    cardBorder: "rgba(255,255,255,0.18)",
    inputBg: "#0d1018",
    inputText: "#f8fafc",
    inputBorder: "rgba(129,140,248,0.55)",
    tagBg: "rgba(129,140,248,0.15)",
    tagText: "#e0e7ff"
  }
};

function getDataCollectionTheme(isDark) {
  return isDark ? DATA_COLLECTION_THEME.dark : DATA_COLLECTION_THEME.light;
}
// Small helpers
function formatLabel(meta, fallbackLabel) {
  if (!meta) return fallbackLabel || "Model";
  const parts = [meta.make, meta.modelName];
  if (meta.streetName) parts.push(meta.streetName);
  if (meta.year !== null && meta.year !== undefined) parts.push(meta.year);
  const base = parts.filter(Boolean).join(" ");
  return meta.region ? `${base} (${meta.region})` : base;
}

function valueToString(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function normalizeCopyField(v) {
  const s = valueToString(v).trim();
  return s.length > 0 ? s : "";
}

function normalizeLower(v) {
  return normalizeCopyField(v).toLowerCase();
}

function escapeSelectorValue(value) {
  const stringValue = String(value ?? "");
  if (typeof window !== "undefined" && window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(stringValue);
  }
  return stringValue.replace(/[^a-zA-Z0-9_-]/g, (char) => `\\${char}`);
}

function extractCopyUpdates(row) {
  if (!row) {
    return { updates: [], hasData: false, hasOEM: false };
  }

  const trimmedOEM = row.OEMNumber ? String(row.OEMNumber).trim() : "";
  const quantityVal = row.Quantity;
  const trimmedNote = row.OEMNote ? String(row.OEMNote).trim() : "";
  const trimmedInfo = row.AdditionalFitmentInfo ? String(row.AdditionalFitmentInfo).trim() : "";
  const trimmedFiche = row.FicheURL ? String(row.FicheURL).trim() : "";

  const updates = [];
  if (trimmedOEM.length > 0) updates.push({ field: "OEMNumber", value: trimmedOEM });
  if (quantityVal !== null && quantityVal !== undefined) updates.push({ field: "Quantity", value: quantityVal });
  if (trimmedNote.length > 0) updates.push({ field: "OEMNote", value: trimmedNote });
  if (trimmedInfo.length > 0) updates.push({ field: "AdditionalFitmentInfo", value: trimmedInfo });
  if (trimmedFiche.length > 0) updates.push({ field: "FicheURL", value: trimmedFiche });

  return {
    updates,
    hasData: updates.length > 0,
    hasOEM: trimmedOEM.length > 0
  };
}

function rowIdentityKey(row) {
  if (!row) return "";
  const fitmentKey = row.FitmentKey ?? row.fitmentKey;
  if (fitmentKey !== null && fitmentKey !== undefined) {
    return `fit:${fitmentKey}`;
  }
  const tempId = row.__tempId ?? row._tempId ?? row.TempId ?? row.tempId;
  if (tempId !== null && tempId !== undefined && String(tempId).length > 0) {
    return `tmp:${tempId}`;
  }
  const modelKey = row.ModelKey ?? row.modelKey ?? "";
  const fitmentLocationKey = row.FitmentLocationKey ?? "";
  const itemTypeKey = row.ItemTypeKey ?? "";
  const section = row._section || (row.IsCurrent ? "current" : "sibling");
  return `${modelKey}:${fitmentLocationKey}:${itemTypeKey}:${section}`;
}

function findRowIndexByIdentity(rows, identity) {
  if (!Array.isArray(rows) || !identity) return -1;
  const target = String(identity);

  for (let idx = 0; idx < rows.length; idx++) {
    if (String(rowIdentityKey(rows[idx])) === target) {
      return idx;
    }
  }

  if (target.startsWith("fit:")) {
    const fitmentKey = target.slice(4);
    for (let idx = 0; idx < rows.length; idx++) {
      const row = rows[idx];
      const fk = row?.FitmentKey ?? row?.fitmentKey;
      if (fk !== null && fk !== undefined && String(fk) === fitmentKey) {
        return idx;
      }
    }
  }

  const parts = target.split(":");
  if (parts.length >= 4) {
    const [modelKey, fitmentLocationKey, itemTypeKey, section] = parts;
    for (let idx = 0; idx < rows.length; idx++) {
      const row = rows[idx];
      if (!row) continue;
      if (String(row.ModelKey ?? row.modelKey ?? "") !== modelKey) continue;
      if (String(row.FitmentLocationKey ?? "") !== fitmentLocationKey) continue;
      if (String(row.ItemTypeKey ?? "") !== itemTypeKey) continue;
      const rowSection = row._section || (row.IsCurrent ? "current" : "sibling");
      if (String(rowSection) !== section) continue;
      return idx;
    }
  }

  return -1;
}

function createKeyForRow(row) {
  if (!row) return "";
  const modelKey = row.ModelKey ?? row.modelKey ?? "";
  const itemTypeKey = row.ItemTypeKey ?? "";
  const fitmentLocationKey = row.FitmentLocationKey ?? "";
  const tempId = row.__tempId ?? row._tempId ?? row.TempId ?? row.tempId ?? "";
  return `${modelKey}-${itemTypeKey}-${fitmentLocationKey}${tempId ? `-${tempId}` : ""}`;
}
function valuesEqual(field, currentValue, incomingValue) {
  if (field === "Quantity") {
    const currentNumber = currentValue === null || currentValue === undefined ? null : Number(currentValue);
    const incomingNumber = incomingValue === null || incomingValue === undefined ? null : Number(incomingValue);
    return currentNumber === incomingNumber;
  }
  return normalizeCopyField(currentValue) === normalizeCopyField(incomingValue);
}

function displayValue(field, value) {
  if (value === null || value === undefined) return "";
  if (field === "Quantity") {
    const num = Number(value);
    return Number.isFinite(num) ? String(num) : String(value);
  }
  return normalizeCopyField(value);
}

function comboKey(row) {
  if (!row) return "";
  const fitmentLocationKey = row.FitmentLocationKey ?? "";
  const itemTypeKey = row.ItemTypeKey ?? "";
  const additionalInfo = normalizeCopyField(row.AdditionalFitmentInfo);
  return `${fitmentLocationKey}|${itemTypeKey}|${additionalInfo}`;
}

function getMetaValue(meta, keys = []) {
  if (!meta) return undefined;
  for (let idx = 0; idx < keys.length; idx++) {
    const key = keys[idx];
    if (meta[key] !== undefined && meta[key] !== null && meta[key] !== "") {
      return meta[key];
    }
  }
  return undefined;
}

function numericEquals(a, b) {
  const aNum = Number(a);
  const bNum = Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    return aNum === bNum;
  }
  return normalizeLower(a) === normalizeLower(b);
}

function matchesModelBase(candidate, base = {}) {
  if (!candidate || !base) return false;
  const make = getMetaValue(candidate, ["Make", "make"]);
  const modelName = getMetaValue(candidate, ["ModelName", "modelName"]);
  const streetName = getMetaValue(candidate, ["StreetName", "streetName"]) ?? "";
  const year = getMetaValue(candidate, ["Year", "year"]);
  const region = getMetaValue(candidate, ["Region", "region"]) ?? "";

  if (normalizeLower(make) !== normalizeLower(base.make)) return false;
  if (normalizeLower(modelName) !== normalizeLower(base.modelName)) return false;
  if (normalizeLower(streetName || "") !== normalizeLower(base.streetName || "")) return false;
  if (!numericEquals(year, base.year)) return false;
  if (normalizeLower(region || "") !== normalizeLower(base.region || "")) return false;
  return true;
}

function normalizeNavCandidate(candidate, base = {}) {
  if (!candidate) return null;
  return {
    ModelKey: getMetaValue(candidate, ["ModelKey", "modelKey"]),
    Make: getMetaValue(candidate, ["Make", "make"]) ?? base.make,
    ModelName: getMetaValue(candidate, ["ModelName", "modelName"]) ?? base.modelName,
    StreetName: getMetaValue(candidate, ["StreetName", "streetName"]) ?? base.streetName ?? "",
    Year: getMetaValue(candidate, ["Year", "year"]) ?? base.year,
    Region: getMetaValue(candidate, ["Region", "region"]) ?? base.region ?? "",
    ModelID: getMetaValue(candidate, ["ModelID", "modelId", "ModelId"]),
    YearID: getMetaValue(candidate, ["YearID", "yearId", "YearId"])
  };
}

function buildDescriptorFromMeta(meta) {
  if (!meta) return null;
  return {
    make: getMetaValue(meta, ["make", "Make"]) ?? "",
    modelName: getMetaValue(meta, ["modelName", "ModelName"]) ?? "",
    streetName: getMetaValue(meta, ["streetName", "StreetName"]) ?? "",
    year: getMetaValue(meta, ["year", "Year"]),
    region: getMetaValue(meta, ["region", "Region"]) ?? ""
  };
}

function descriptorToParams(descriptor) {
  if (!descriptor) return null;
  const { make, modelName, streetName, year, region } = descriptor;
  if (!make || !modelName || year === null || year === undefined) {
    return null;
  }
  const params = { make, modelName, year };
  if (streetName) params.streetName = streetName;
  if (region) params.region = region;
  return params;
}

function mergeMetaWithCandidate(meta, candidate, labelFallback) {
  if (!candidate) {
    const fallback = { ...meta };
    if (labelFallback && !fallback.label) {
      fallback.label = labelFallback;
    }
    return fallback;
  }

  const merged = { ...meta };
  const modelKey = candidate.ModelKey ?? candidate.modelKey ?? merged.modelKey ?? merged.ModelKey ?? null;
  if (modelKey !== null && modelKey !== undefined) {
    merged.modelKey = modelKey;
    merged.ModelKey = modelKey;
  }

  const make = candidate.Make ?? candidate.make ?? merged.make ?? merged.Make;
  if (make !== undefined) {
    merged.make = make;
    merged.Make = make;
  }

  const modelName = candidate.ModelName ?? candidate.modelName ?? merged.modelName ?? merged.ModelName;
  if (modelName !== undefined) {
    merged.modelName = modelName;
    merged.ModelName = modelName;
  }

  const streetName = candidate.StreetName ?? candidate.streetName ?? merged.streetName ?? merged.StreetName;
  if (streetName !== undefined) {
    merged.streetName = streetName;
    merged.StreetName = streetName;
  }

  const year = candidate.Year ?? candidate.year ?? merged.year ?? merged.Year;
  if (year !== undefined) {
    merged.year = year;
    merged.Year = year;
  }

  const region = candidate.Region ?? candidate.region ?? merged.region ?? merged.Region;
  if (region !== undefined) {
    merged.region = region;
    merged.Region = region;
  }

  const modelId = candidate.ModelID ?? candidate.modelId ?? candidate.ModelId ?? merged.ModelID ?? merged.modelId ?? merged.modelID;
  if (modelId !== undefined) {
    merged.ModelID = modelId;
    merged.modelId = modelId;
  }

  const yearId = candidate.YearID ?? candidate.yearId ?? candidate.YearId ?? merged.YearID ?? merged.yearId ?? merged.YearId;
  if (yearId !== undefined) {
    merged.YearID = yearId;
    merged.yearId = yearId;
  }

  merged.label = formatLabel(merged, labelFallback);
  return merged;
}

function hasValue(val) {
  return !(val === null || val === undefined || val === "");
}

function firstValueFromRows(rows, keys = []) {
  if (!Array.isArray(rows)) return undefined;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row) continue;
    for (let k = 0; k < keys.length; k++) {
      const key = keys[k];
      const value = row[key];
      if (hasValue(value)) {
        return value;
      }
    }
  }
  return undefined;
}

const EDITABLE_FIELD_ORDER = [
  "SKUGroup",
  "OEMNumber",
  "Quantity",
  "OEMNote",
  "AdditionalFitmentInfo",
  "FicheURL"
];

const ARROW_KEYS = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]);
const MODEL_LOOKUP_PAGE_SIZE = 500; // defensive client-side paging because /api/models ignores filters today
const MODEL_LOOKUP_MAX_PAGES = 8;
function toolbarStyle(theme) {
  return {
    padding: "0.75rem 1.25rem",
    display: "flex",
    gap: "1rem",
    borderBottom: `1px solid ${theme.toolbarBorder}`,
    background: theme.toolbarBg,
    color: theme.text,
    flexWrap: "wrap"
  };
}

function headerStyle(theme) {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "1rem 1.25rem",
    background: theme.headerBg,
    color: theme.text,
    borderBottom: `1px solid ${theme.border}`
  };
}

function bodyContainerStyle(theme) {
  return {
    flex: 1,
    overflow: "hidden",
    background: theme.shell,
    display: "flex",
    flexDirection: "column",
    position: "relative"
  };
}

const historyOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.45)",
  zIndex: 2100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1.5rem"
};

const bulkCopyOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  zIndex: 2200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1.5rem"
};

const navChoiceOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  zIndex: 2125,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1.5rem"
};

function historyPanelStyle(theme) {
  return {
    width: "min(80vw, 1400px)",
    maxHeight: "85vh",
    overflowY: "auto",
    background: theme.elevatedSurface,
    color: theme.text,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: 12,
    boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
    padding: "1.25rem",
    position: "relative"
  };
}

function bulkCopyPanelStyle(theme) {
  return {
    width: "90vw",
    maxWidth: "90vw",
    maxHeight: "88vh",
    overflowY: "auto",
    background: theme.elevatedSurface,
    color: theme.text,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: 12,
    boxShadow: "0 18px 48px rgba(0,0,0,0.4)",
    padding: "1.5rem",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "1.25rem"
  };
}

function navChoicePanelStyle(theme) {
  return {
    width: "min(85vw, 960px)",
    maxHeight: "80vh",
    overflowY: "auto",
    background: theme.elevatedSurface,
    color: theme.text,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: 12,
    boxShadow: "0 18px 48px rgba(0,0,0,0.4)",
    padding: "1.25rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem"
  };
}

const bulkCopyCardGridStyle = {
  display: "grid",
  gap: "1rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))"
};

const bulkCopyRadioGridStyle = {
  display: "grid",
  gap: "0.75rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  alignItems: "stretch"
};

const tableWrapStyle = { flex: 1, overflow: "auto" };
const tableStyle = (theme) => ({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.95rem",
  color: theme.text
});
const thSticky = (theme) => ({
  position: "sticky",
  top: 0,
  background: theme.tableHeaderBg,
  zIndex: 1,
  padding: "0.5rem",
  textAlign: "left",
  borderBottom: `1px solid ${theme.border}`,
  whiteSpace: "nowrap",
  color: theme.text
});
const thFilter = (theme) => ({
  position: "sticky",
  top: "2.25rem",
  background: theme.tableFilterBg,
  zIndex: 1,
  padding: "0.35rem 0.5rem",
  borderBottom: `1px solid ${theme.borderMuted}`
});
const tdCell = (theme) => ({
  padding: "0.5rem",
  borderBottom: `1px solid ${theme.rowDivider}`,
  verticalAlign: "top",
  color: theme.text,
  background: "transparent"
});
const tdCellActions = (theme) => ({ ...tdCell(theme), textAlign: "left", whiteSpace: "nowrap" });
const inputCellStyle = (theme) => ({
  width: "100%",
  padding: "4px 8px",
  boxSizing: "border-box",
  background: theme.inputBg,
  color: theme.inputText,
  border: `1px solid ${theme.inputBorder}`,
  borderRadius: 4
});
const dialogBackdropStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.45)",
  zIndex: 2000,
  display: "flex",
  alignItems: "stretch",
  justifyContent: "stretch"
};

function dialogPanelStyle(theme) {
  return {
    width: "100vw",
    height: "100vh",
    maxWidth: "100vw",
    maxHeight: "100vh",
    background: theme.surface,
    color: theme.text,
    borderRadius: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 16px 40px rgba(0,0,0,0.35)"
  };
}

function rowStyle(row, prevRow, theme) {
  const style = {
    background: row?.IsCurrent ? theme.rowCurrentBg : theme.rowSiblingBg,
    color: theme.text,
    opacity: 1
  };

  if (row?._section === "previous" || row?._section === "next") {
    style.background = theme.prevNextBg;
  } else if (row?.IsGhost || row?.isGhost) {
    style.background = theme.ghostBg;
    style.opacity = 0.95;
  }

  const itemTypeBorderColor = theme.itemDivider;
  const locationBorderColor = theme.locationDivider;

  if (!prevRow || prevRow.ItemType !== row.ItemType) {
    style.borderTop = `4px solid ${itemTypeBorderColor}`;
  } else if (prevRow.FitmentLocation !== row.FitmentLocation) {
    style.borderTop = `2.5px solid ${locationBorderColor}`;
  }

  return style;
}

function hasSiblingRowData(row) {
  if (!row) return false;

  const richFields = [
    "OEMNumber",
    "OEMNote",
    "AdditionalFitmentInfo",
    "FicheURL",
    "SKUGroup",
    "SKU",
    "SupplierPartNumber"
  ];

  if (row.Quantity !== null && row.Quantity !== undefined) {
    return true;
  }

  return richFields.some((field) => {
    const value = row[field];
  return value !== null && value !== undefined && valueToString(value).trim().length > 0;
  });
}

const collator = new Intl.Collator("en", { sensitivity: "base", numeric: false });

function isGhostRow(row) {
  return !!(row?.IsGhost || row?.isGhost);
}

function rowHasMeaningfulData(row) {
  if (!row) return false;
  if (row.FitmentKey) return true;
  if (row.Quantity !== null && row.Quantity !== undefined) return true;
  if (normalizeCopyField(row.OEMNumber).length > 0) return true;
  if (normalizeCopyField(row.SKUGroup).length > 0) return true;
  if (normalizeCopyField(row.OEMNote).length > 0) return true;
  if (normalizeCopyField(row.AdditionalFitmentInfo).length > 0) return true;
  if (normalizeCopyField(row.FicheURL).length > 0) return true;
  if (normalizeCopyField(row.SKU).length > 0) return true;
  if (normalizeCopyField(row.SupplierPartNumber).length > 0) return true;
  return hasSiblingRowData(row);
}

function pickPreferredRow(existing, candidate) {
  if (!existing) return candidate;
  if (!candidate) return existing;

  const existingGhost = isGhostRow(existing);
  const candidateGhost = isGhostRow(candidate);
  if (existingGhost && !candidateGhost) return candidate;
  if (!existingGhost && candidateGhost) return existing;

  const existingHasData = rowHasMeaningfulData(existing);
  const candidateHasData = rowHasMeaningfulData(candidate);
  if (!existingHasData && candidateHasData) return candidate;
  if (existingHasData && !candidateHasData) return existing;

  const existingUpdated = existing.DateLastUpdated ? new Date(existing.DateLastUpdated).getTime() : 0;
  const candidateUpdated = candidate.DateLastUpdated ? new Date(candidate.DateLastUpdated).getTime() : 0;
  if (candidateUpdated > existingUpdated) return candidate;
  if (existingUpdated > candidateUpdated) return existing;

  return candidate;
}

function compareRows(a, b) {
  const itA = normalizeCopyField(a.ItemType);
  const itB = normalizeCopyField(b.ItemType);
  const itCompare = collator.compare(itA, itB);
  if (itCompare !== 0) return itCompare;

  const flA = normalizeCopyField(a.FitmentLocation);
  const flB = normalizeCopyField(b.FitmentLocation);
  const flCompare = collator.compare(flA, flB);
  if (flCompare !== 0) return flCompare;

  const addA = normalizeCopyField(a.AdditionalFitmentInfo);
  const addB = normalizeCopyField(b.AdditionalFitmentInfo);
  if (addA.length === 0 && addB.length > 0) return 1;
  if (addA.length > 0 && addB.length === 0) return -1;
  const addCompare = collator.compare(addA, addB);
  if (addCompare !== 0) return addCompare;

  const secOrder = (section) => {
    if (section === "current") return 0;
    if (section === "previous") return -1;
    if (section === "next") return 1;
    return 2;
  };
  const secA = secOrder(a._section);
  const secB = secOrder(b._section);
  if (secA !== secB) return secA - secB;

  const yA = a.Year !== null && a.Year !== undefined ? Number(a.Year) : 0;
  const yB = b.Year !== null && b.Year !== undefined ? Number(b.Year) : 0;
  return yA - yB;
}

// Build the OEM note for a kit from its components, e.g. "12345-ABC x2, 67890-DEF x1".
function buildKitOEMNote(components) {
  if (!Array.isArray(components)) return "";
  const parts = components
    .map((component) => {
      if (!component) return null;
      const oem = valueToString(
        component.ComponentOEMNumber ?? component.OEMNumber ?? component.oemNumber
      ).trim();
      if (!oem) return null;
      const qty = Number(component.Quantity ?? component.quantity);
      return Number.isFinite(qty) && qty > 0 ? `${oem} x${qty}` : oem;
    })
    .filter(Boolean);
  return parts.join(", ");
}

export default function DataCollectionOverviewByModel(props) {
  const {
    modelKey: modelKeyProp,
    onClose,
    permissions = {},
    isDark,
    label: initialLabel,
    initialItemType,
    formatToggle: sharedFormatToggle,
    onFormatToggleChange,
    onRequestYearChange,
    resolveOEMTypeFast
  } = props;

  // Allow this component to be used both as a standalone page/modal (parent controls modelKey)
  // and as an embedded popup (no onRequestYearChange provided).
  const [activeModelKey, setActiveModelKey] = useState(modelKeyProp);
  useEffect(() => {
    setActiveModelKey(modelKeyProp);
  }, [modelKeyProp]);

  const modelKey = activeModelKey;

  const canWrite = permissions?.["Data Collection/Reviewing"] === "write";
  const canManageKits = permissions?.Kitting === "write" || canWrite;
  const theme = useMemo(() => getDataCollectionTheme(isDark), [isDark]);

  const [localFormatToggle, setLocalFormatToggle] = useState(
    sharedFormatToggle === undefined ? false : !!sharedFormatToggle
  );

  useEffect(() => {
    if (sharedFormatToggle === undefined) return;
    setLocalFormatToggle(!!sharedFormatToggle);
  }, [sharedFormatToggle]);

  const formatOEMEnabled =
    sharedFormatToggle === undefined ? localFormatToggle : !!sharedFormatToggle;

  const updateFormatToggle = useCallback(
    (next) => {
      if (typeof onFormatToggleChange === "function") {
        onFormatToggleChange(next);
      } else {
        setLocalFormatToggle(next);
      }
    },
    [onFormatToggleChange]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copyingKey, setCopyingKey] = useState("");
  const [copyingSection, setCopyingSection] = useState("");
  const [showPrevNext, setShowPrevNext] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [hideCompleted, setHideCompleted] = useState(false);
  const oemTypeCacheRef = useRef(new Map());
  const [oemTypeCacheTick, setOemTypeCacheTick] = useState(0);
  const [filters, setFilters] = useState({
    ItemType: typeof initialItemType === "string" ? initialItemType : "",
    FitmentLocation: "",
    SKUGroup: "",
    OEMNumber: "",
    Quantity: "",
    OEMNote: "",
    AdditionalFitmentInfo: "",
    FicheURL: ""
  });

  const [modelSearchQuery, setModelSearchQuery] = useState("");
  const [modelSearchOpen, setModelSearchOpen] = useState(false);
  const [modelSearchRows, setModelSearchRows] = useState([]);
  const [modelSearchLoading, setModelSearchLoading] = useState(false);
  const [modelSearchError, setModelSearchError] = useState("");
  const modelSearchWrapRef = useRef(null);

  // Recent models UI:
  // - Back/Forward uses a navigation stack (can traverse through actual visit order)
  // - Dropdown uses an MRU list (unique models, most recently visited at top)
  const [recentModels, setRecentModels] = useState([]);
  const [navStack, setNavStack] = useState([]);
  const [navIndex, setNavIndex] = useState(-1);
  const navStackRef = useRef([]);
  const navIndexRef = useRef(-1);
  const navActionRef = useRef(null); // { type: 'visit'|'stack', targetIndex?: number, targetKey?: any, startedAt?: number }

  useEffect(() => {
    if (typeof initialItemType !== "string") return;
    const trimmed = initialItemType.trim();
    if (!trimmed) return;
    setFilters((prev) => ({ ...prev, ItemType: trimmed }));
  }, [initialItemType]);
  const [navLookupDirection, setNavLookupDirection] = useState("");
  const [navChoice, setNavChoice] = useState(null);
  const navChoiceResolverRef = useRef(null);

  const [rowsState, setRowsState] = useState([]);
  const rowsStateRef = useRef([]);
  const filteredRowsRef = useRef([]);
  const tempRowCounterRef = useRef(0);
  const createInFlightRef = useRef(new Set());
  const pendingCreateEditsRef = useRef(new Map());
  const [editing, setEditing] = useState({ rowIdentity: "", field: "", value: "" });
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyFitmentKey, setHistoryFitmentKey] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [selectedCell, setSelectedCell] = useState({ rowIdentity: "", field: "" });
  const [manageOEMOpen, setManageOEMOpen] = useState(false);
  const [manageSKUOpen, setManageSKUOpen] = useState(false);
  const [manageInitialOEMNumber, setManageInitialOEMNumber] = useState("");
  const [manageInitialOEMKey, setManageInitialOEMKey] = useState(null);
  const [manageInitialSKUGroup, setManageInitialSKUGroup] = useState("");
  const [kitModalState, setKitModalState] = useState({ open: false, kitId: null, fitment: null });
  const [selectedKitRowIds, setSelectedKitRowIds] = useState(() => new Set());
  const [kitBuilder, setKitBuilder] = useState(null);
  const [oneOffContext, setOneOffContext] = useState(null);
  const [bulkCopyReview, setBulkCopyReview] = useState(null);
  const bulkCopyResolverRef = useRef(null);
  const [bulkCopyDecisions, setBulkCopyDecisions] = useState({});
  const modelMetaRequestsRef = useRef(new Set());
  const [modelMetaMap, setModelMetaMap] = useState({});
  const modelMetaMapRef = useRef({});
  const modelDetailRequestsRef = useRef(new Map());
  const modelBlockRequestsRef = useRef(new Map());

  const focusEditableCell = useCallback((rowId, field) => {
    if (!rowId || !field) return false;
    const escapedRow = escapeSelectorValue(rowId);
    const escapedField = escapeSelectorValue(field);
    const selector = `input[data-row-identity="${escapedRow}"][data-field="${escapedField}"]`;
    const element = typeof document !== "undefined" ? document.querySelector(selector) : null;
    if (element) {
      element.focus();
      if (typeof element.select === "function") {
        element.select();
      }
      return true;
    }
    return false;
  }, []);

  const scheduleFocusEditableCell = useCallback(
    (rowId, field) => {
      if (!rowId || !field) return;
      if (focusEditableCell(rowId, field)) return;
      setTimeout(() => {
        focusEditableCell(rowId, field);
      }, 30);
    },
    [focusEditableCell]
  );

  const findNextEmptyOEMIdentity = useCallback(
    (fromRowId) => {
      // Use filtered rows to ensure navigation only goes to visible rows
      const rows = filteredRowsRef.current || rowsStateRef.current || [];
      const startIndex = findRowIndexByIdentity(rows, fromRowId);
      const begin = startIndex >= 0 ? startIndex + 1 : 0;
      for (let idx = begin; idx < rows.length; idx++) {
        const candidate = rows[idx];
        if (!candidate) continue;
        const section = candidate._section || (candidate.IsCurrent ? "current" : "sibling");
        if (section !== "current") continue;
        if (normalizeCopyField(candidate.OEMNumber).length > 0) continue;
        return rowIdentityKey(candidate);
      }
      return "";
    },
    []
  );

  const findAdjacentEditableRow = useCallback((fromRowId, direction) => {
    if (direction !== 1 && direction !== -1) return "";
    // Use filtered rows to ensure navigation only goes to visible rows
    const rows = filteredRowsRef.current || rowsStateRef.current || [];
    const startIndex = findRowIndexByIdentity(rows, fromRowId);
    if (startIndex < 0) return "";
    for (let idx = startIndex + direction; idx >= 0 && idx < rows.length; idx += direction) {
      const candidate = rows[idx];
      if (!candidate) continue;
      const section = candidate._section || (candidate.IsCurrent ? "current" : "sibling");
      if (section !== "current") continue;
      return rowIdentityKey(candidate);
    }
    return "";
  }, []);

  const focusNextAvailableOEM = useCallback(
    (referenceId, fallbackField = "OEMNumber") => {
      if (!referenceId) return;
      const nextIdentity = findNextEmptyOEMIdentity(referenceId);
      if (nextIdentity) {
        scheduleFocusEditableCell(nextIdentity, "OEMNumber");
      } else {
        scheduleFocusEditableCell(referenceId, fallbackField);
      }
    },
    [findNextEmptyOEMIdentity, scheduleFocusEditableCell]
  );

  const requestBulkCopyDecisions = useCallback((payload) => {
    return new Promise((resolve, reject) => {
      bulkCopyResolverRef.current = { resolve, reject };
      setBulkCopyReview(payload);
    });
  }, []);

  const handleBulkCopyReviewConfirm = useCallback((decisions) => {
    if (bulkCopyResolverRef.current) {
      bulkCopyResolverRef.current.resolve(decisions);
      bulkCopyResolverRef.current = null;
    }
    setBulkCopyReview(null);
  }, []);

  const handleBulkCopyReviewCancel = useCallback(() => {
    if (bulkCopyResolverRef.current) {
      bulkCopyResolverRef.current.reject(new Error("Bulk copy cancelled"));
      bulkCopyResolverRef.current = null;
    }
    setBulkCopyReview(null);
  }, []);

  useEffect(() => {
    if (!bulkCopyReview) {
      setBulkCopyDecisions({});
      return;
    }

    const defaults = {};
    if (Array.isArray(bulkCopyReview.conflicts)) {
      bulkCopyReview.conflicts.forEach((item) => {
        defaults[item.id] = item.defaultDecision || "override";
      });
    }
    if (Array.isArray(bulkCopyReview.extras)) {
      bulkCopyReview.extras.forEach((item) => {
        defaults[item.id] = item.defaultDecision || "add";
      });
    }
    setBulkCopyDecisions(defaults);
  }, [bulkCopyReview]);

  const updateBulkCopyDecision = useCallback((id, decision) => {
    setBulkCopyDecisions((prev) => {
      if (prev[id] === decision) return prev;
      return { ...prev, [id]: decision };
    });
  }, []);

  const bulkCopyTotals = useMemo(() => {
    if (!bulkCopyReview) {
      return { conflicts: 0, extras: 0, total: 0 };
    }

    const conflicts = Array.isArray(bulkCopyReview.conflicts) ? bulkCopyReview.conflicts.length : 0;
    const extras = Array.isArray(bulkCopyReview.extras) ? bulkCopyReview.extras.length : 0;
    return { conflicts, extras, total: conflicts + extras };
  }, [bulkCopyReview]);

  const confirmBulkCopyReview = useCallback(() => {
    handleBulkCopyReviewConfirm(bulkCopyDecisions);
  }, [bulkCopyDecisions, handleBulkCopyReviewConfirm]);

  const canConfirmBulkCopy =
    bulkCopyTotals.total === 0 || Object.keys(bulkCopyDecisions).length >= bulkCopyTotals.total;

  useEffect(() => {
    setRowsState([]);
    rowsStateRef.current = [];
    setData(null);
    setCopyingKey("");
    setCopyingSection("");
    setHistoryVisible(false);
    setHistoryRecords([]);
    setHistoryFitmentKey(null);
    setHistoryError("");
    setSelectedCell({ rowIdentity: "", field: "" });
    setManageOEMOpen(false);
    setManageSKUOpen(false);
    setManageInitialOEMNumber("");
    setManageInitialOEMKey(null);
    setManageInitialSKUGroup("");
    setOneOffContext(null);
    setSelectedKitRowIds(new Set());
    setKitBuilder(null);
  }, [modelKey]);

  useEffect(() => {
    if (!showPrevNext) {
      setCopyingSection("");
    }
  }, [showPrevNext]);

  // F2 keyboard shortcut to toggle show previous/next year data
  // F3 keyboard shortcut to toggle hide completed lines
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        setShowPrevNext((prev) => !prev);
      } else if (e.key === "F3") {
        e.preventDefault();
        setHideCompleted((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  // Pre-fetch OEMTypes when hideCompleted is enabled to ensure correct filtering
  // Use a ref to store the resolver to avoid effect re-runs when function reference changes
  const resolveOEMTypeFastRef = useRef(resolveOEMTypeFast);
  useEffect(() => {
    resolveOEMTypeFastRef.current = resolveOEMTypeFast;
  }, [resolveOEMTypeFast]);

  useEffect(() => {
    if (!hideCompleted) return;
    
    const oemNumbersToFetch = [];
    rowsState.forEach((row) => {
      if (!row.IsCurrent) return;
      const oemNumber = normalizeCopyField(row.OEMNumber);
      if (oemNumber && !oemTypeCacheRef.current.has(oemNumber)) {
        oemNumbersToFetch.push(oemNumber);
      }
    });
    
    // If cache is already populated for all OEM numbers, trigger a tick anyway
    // to ensure the filter runs with the cached data
    if (oemNumbersToFetch.length === 0) {
      // Small delay to ensure this runs after the initial render
      const timer = setTimeout(() => {
        setOemTypeCacheTick((t) => t + 1);
      }, 0);
      return () => clearTimeout(timer);
    }
    
    let isMounted = true;
    const fetchPromise = (async () => {
      const resolver = resolveOEMTypeFastRef.current;
      for (const oemNumber of oemNumbersToFetch) {
        try {
          // Try using the provided resolver first
          if (typeof resolver === "function") {
            const oemType = await resolver(oemNumber);
            // Cache even if null (use "__UNKNOWN__" marker to distinguish from "not fetched")
            oemTypeCacheRef.current.set(oemNumber, oemType || "__UNKNOWN__");
          } else {
            // Fallback: make direct API call if resolver not provided
            try {
              const { data } = await axios.get('/api/oem/type', { params: { number: oemNumber } });
              const oemType = data?.oemType ?? null;
              oemTypeCacheRef.current.set(oemNumber, oemType || "__UNKNOWN__");
            } catch (apiErr) {
              // Cache as unknown on error
              oemTypeCacheRef.current.set(oemNumber, "__UNKNOWN__");
            }
          }
        } catch (err) {
          // Cache as unknown on error to prevent repeated fetches
          oemTypeCacheRef.current.set(oemNumber, "__UNKNOWN__");
        }
      }
      // Always trigger re-render after fetching - the cache data is valid
      // even if the effect was cleaned up, and we need the UI to update
      if (isMounted) {
        setOemTypeCacheTick((t) => t + 1);
      }
    })();
    
    return () => { isMounted = false; };
  }, [hideCompleted, rowsState]);

  // Pre-fetch OEMTypes when data loads (not just when hideCompleted is toggled)
  // This ensures OEM types are available immediately when user toggles hide
  useEffect(() => {
    if (!data) return;
    
    const allRows = [
      ...(Array.isArray(data?.current?.rows) ? data.current.rows : []),
      ...(Array.isArray(data?.previous?.rows) ? data.previous.rows : []),
      ...(Array.isArray(data?.next?.rows) ? data.next.rows : [])
    ];
    
    const oemNumbersToFetch = [];
    allRows.forEach((row) => {
      const oemNumber = normalizeCopyField(row?.OEMNumber);
      if (oemNumber && !oemTypeCacheRef.current.has(oemNumber)) {
        oemNumbersToFetch.push(oemNumber);
      }
    });
    
    if (oemNumbersToFetch.length === 0) return;
    
    // Deduplicate
    const uniqueOemNumbers = [...new Set(oemNumbersToFetch)];
    
    let isMounted = true;
    (async () => {
      const resolver = resolveOEMTypeFastRef.current;
      for (const oemNumber of uniqueOemNumbers) {
        if (!isMounted) break;
        try {
          if (typeof resolver === "function") {
            const oemType = await resolver(oemNumber);
            oemTypeCacheRef.current.set(oemNumber, oemType || "__UNKNOWN__");
          } else {
            try {
              const { data: resp } = await axios.get('/api/oem/type', { params: { number: oemNumber } });
              const oemType = resp?.oemType ?? null;
              oemTypeCacheRef.current.set(oemNumber, oemType || "__UNKNOWN__");
            } catch (apiErr) {
              oemTypeCacheRef.current.set(oemNumber, "__UNKNOWN__");
            }
          }
        } catch (err) {
          oemTypeCacheRef.current.set(oemNumber, "__UNKNOWN__");
        }
      }
      // Trigger re-render so hide logic can use the populated cache
      if (isMounted) {
        setOemTypeCacheTick((t) => t + 1);
      }
    })();
    
    return () => { isMounted = false; };
  }, [data]);

  useEffect(() => {
    if (!modelKey) return;

    const controller = new AbortController();
    setLoading(true);
    setError("");

    const url = `/api/data-collection-model/${modelKey}`;

    axios
      .get(url, { signal: controller.signal, timeout: 60000 })
      .then((res) => {
        if (controller.signal.aborted) return;
        setData(res?.data || null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (axios.isCancel && axios.isCancel(err)) return;
        console.error("Failed to load model overview", err);
        setError(
          err?.code === "ECONNABORTED"
            ? "Loading took longer than 60 seconds and was stopped. Please try again."
            : err?.response?.data?.error || "Failed to load model data."
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [modelKey, refreshTick]);

  const currentMeta = data?.current?.meta || null;
  const previousMeta = data?.previous?.meta || null;
  const nextMeta = data?.next?.meta || null;

  const currentRows = useMemo(() => {
    const rows = data?.current?.rows;
    return Array.isArray(rows) ? rows : [];
  }, [data]);

  const previousRows = useMemo(() => {
    const rows = data?.previous?.rows;
    return Array.isArray(rows) ? rows : [];
  }, [data]);

  const nextRows = useMemo(() => {
    const rows = data?.next?.rows;
    return Array.isArray(rows) ? rows : [];
  }, [data]);

  const headerLabel = useMemo(
    () => formatLabel(currentMeta, initialLabel),
    [currentMeta, initialLabel]
  );

  const displayedModelKey = currentMeta?.modelKey ?? modelKey ?? null;
  const previousModelKey = getMetaValue(previousMeta, ["modelKey", "ModelKey"]);
  const nextModelKey = getMetaValue(nextMeta, ["modelKey", "ModelKey"]);
  const hasPreviousModel = !!previousModelKey;
  const hasNextModel = !!nextModelKey;

  useEffect(() => {
    navStackRef.current = navStack;
  }, [navStack]);

  useEffect(() => {
    navIndexRef.current = navIndex;
  }, [navIndex]);

  const buildRecentEntry = useCallback(
    (key) => {
      const label = headerLabel || `Model ${key}`;
      const make = getMetaValue(currentMeta, ["make", "Make"]) ?? "";
      const modelName = getMetaValue(currentMeta, ["modelName", "ModelName"]) ?? "";
      const streetName = getMetaValue(currentMeta, ["streetName", "StreetName"]) ?? "";
      const year = getMetaValue(currentMeta, ["year", "Year"]) ?? null;
      const region = getMetaValue(currentMeta, ["region", "Region"]) ?? "";

      return {
        modelKey: key,
        label,
        meta: {
          ModelKey: key,
          modelKey: key,
          Make: make,
          ModelName: modelName,
          StreetName: streetName,
          Year: year,
          Region: region,
          make,
          modelName,
          streetName,
          year,
          region
        }
      };
    },
    [currentMeta, headerLabel]
  );

  useEffect(() => {
    const key = displayedModelKey;
    if (!key) return;

    // Only add a model to the recent list if it's not already present.
    // (Do NOT move the current selection to the top; this keeps history stable
    // and prevents Forward history from being erased by "re-visits".)
    const entry = buildRecentEntry(key);
    setRecentModels((prev) => {
      const base = Array.isArray(prev) ? prev : [];
      const exists = base.some((e) => String(e.modelKey) === String(entry.modelKey));
      if (exists) return base;
      const next = [entry, ...base];
      if (next.length > 10) next.length = 10;
      return next;
    });

    // If this navigation was initiated by our own nav handlers, don't double-record.
    // Important: only clear the action once we actually reach its target modelKey.
    const action = navActionRef.current;
    if (action && (action.type === "visit" || action.type === "stack")) {
      const startedAt = typeof action.startedAt === "number" ? action.startedAt : null;
      const isStale = startedAt != null ? Date.now() - startedAt > 15000 : false;
      const targetKey = action.targetKey;
      if (isStale || !targetKey || String(targetKey) === String(key)) {
        navActionRef.current = null;
      }
      return;
    }

    // External model changes (or initial load): treat as a visit.
    {
      const base = Array.isArray(navStackRef.current) ? navStackRef.current : [];
      const idx = navIndexRef.current;

      // If the model already exists in the stack, treat it as a history jump
      // (do not truncate/re-push, which would erase Forward history).
      const existingIndex = base.findIndex((k) => String(k) === String(key));
      if (existingIndex >= 0) {
        const nextIndex = Math.max(0, Math.min(existingIndex, base.length - 1));
        navStackRef.current = base;
        navIndexRef.current = nextIndex;
        setNavStack(base);
        setNavIndex(nextIndex);
      } else {
        // Record a new visit: truncate Forward history if we're not at the end.
        const truncated = idx >= 0 && idx < base.length - 1 ? base.slice(0, idx + 1) : base.slice();
        const last = truncated[truncated.length - 1];

        let nextStack = truncated;
        if (!last || String(last) !== String(key)) {
          nextStack = [...truncated, key];
        }

        if (nextStack.length > 10) {
          nextStack = nextStack.slice(nextStack.length - 10);
        }

        const computedIndex = nextStack.findIndex((k) => String(k) === String(key));
        const nextIndex = computedIndex >= 0 ? computedIndex : Math.max(0, nextStack.length - 1);

        navStackRef.current = nextStack;
        navIndexRef.current = nextIndex;
        setNavStack(nextStack);
        setNavIndex(nextIndex);
      }
    }
  }, [displayedModelKey, buildRecentEntry]);

  const modelSearchContext = useMemo(() => buildDescriptorFromMeta(currentMeta), [currentMeta]);
  const modelSearchMake = modelSearchContext?.make || "";
  const modelSearchModelName = modelSearchContext?.modelName || "";
  const modelSearchStreetName = modelSearchContext?.streetName || "";
  const modelSearchRegion = modelSearchContext?.region || "";

  const fetchContextModels = useCallback(async () => {
    if (!modelSearchMake || !modelSearchModelName) return;
    setModelSearchLoading(true);
    setModelSearchError("");
    try {
      const resp = await axios.get("/api/models/context-list", {
        params: {
          make: modelSearchMake,
          modelName: modelSearchModelName,
          limit: 200
        }
      });
      setModelSearchRows(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      setModelSearchError(e?.response?.data?.error || e?.message || "Failed to load models");
      setModelSearchRows([]);
    } finally {
      setModelSearchLoading(false);
    }
  }, [modelSearchMake, modelSearchModelName]);

  const fetchSearchModels = useCallback(
    async (q) => {
      const query = String(q || "").trim();
      if (!query) {
        await fetchContextModels();
        return;
      }

      setModelSearchLoading(true);
      setModelSearchError("");
      try {
        const resp = await axios.get("/api/models/search", {
          params: {
            query,
            limit: 50,
            contextMake: modelSearchMake,
            contextModelName: modelSearchModelName,
            contextStreetName: modelSearchStreetName,
            contextRegion: modelSearchRegion
          }
        });
        setModelSearchRows(Array.isArray(resp.data) ? resp.data : []);
      } catch (e) {
        setModelSearchError(e?.response?.data?.error || e?.message || "Search failed");
        setModelSearchRows([]);
      } finally {
        setModelSearchLoading(false);
      }
    },
    [
      fetchContextModels,
      modelSearchMake,
      modelSearchModelName,
      modelSearchRegion,
      modelSearchStreetName
    ]
  );

  useEffect(() => {
    if (!modelSearchOpen) return;
    const handle = setTimeout(() => {
      fetchSearchModels(modelSearchQuery);
    }, 150);
    return () => clearTimeout(handle);
  }, [modelSearchQuery, modelSearchOpen, fetchSearchModels]);

  useEffect(() => {
    if (!modelSearchOpen) return;
    const onDocMouseDown = (e) => {
      if (!modelSearchWrapRef.current) return;
      if (!modelSearchWrapRef.current.contains(e.target)) {
        setModelSearchOpen(false);
      }
    };
    const onDocKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setModelSearchOpen(false);
      }
    };

    // Use capture so this still fires even though the modal stops event bubbling.
    document.addEventListener("mousedown", onDocMouseDown, true);
    document.addEventListener("keydown", onDocKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown, true);
      document.removeEventListener("keydown", onDocKeyDown, true);
    };
  }, [modelSearchOpen]);

  useEffect(() => {
    // When the active model changes, close the dropdown.
    setModelSearchOpen(false);
  }, [modelKey]);

  const formatModelSearchLabel = useCallback((row) => {
    if (!row) return "";
    const make = row.Make ?? "";
    const modelName = row.ModelName ?? "";
    const streetName = row.StreetName ? ` ${row.StreetName}` : "";
    const year = row.Year !== null && row.Year !== undefined ? ` ${row.Year}` : "";
    const region = row.Region ? ` (${row.Region})` : "";
    return `${make} ${modelName}${streetName}${year}${region}`.trim();
  }, []);

  const jumpToModel = useCallback(
    (row) => {
      const nextKey = row?.ModelKey ?? row?.modelKey ?? row?.ModelKey;
      if (!nextKey) return;

      if (String(nextKey) === String(modelKey)) {
        setModelSearchOpen(false);
        return;
      }

      setModelSearchOpen(false);
      setModelSearchQuery("");

      // If the model is already in the history stack, treat this as a history jump
      // (do not push/truncate, which would erase Forward history).
      const existingStack = navStackRef.current;
      const existingIndex = Array.isArray(existingStack)
        ? existingStack.findIndex((k) => String(k) === String(nextKey))
        : -1;
      if (existingIndex >= 0) {
        navActionRef.current = { type: "stack", targetIndex: existingIndex, targetKey: nextKey, startedAt: Date.now() };
        const nextIndex = Math.min(9, existingIndex);
        navIndexRef.current = nextIndex;
        setNavIndex(nextIndex);
        const payload = row?.ModelKey ? row : { ...(row || {}), ModelKey: nextKey, modelKey: nextKey };
        if (typeof onRequestYearChange === "function") {
          onRequestYearChange(payload);
        } else {
          setActiveModelKey(nextKey);
        }
        return;
      }

      // Record visit in nav stack (truncate forward if needed).
      navActionRef.current = { type: "visit", targetKey: nextKey, startedAt: Date.now() };
      {
        const base = Array.isArray(navStackRef.current) ? navStackRef.current : [];
        const idx = navIndexRef.current;
        const truncated = idx >= 0 && idx < base.length - 1 ? base.slice(0, idx + 1) : base.slice();
        const last = truncated[truncated.length - 1];

        let nextStack = truncated;
        if (!last || String(last) !== String(nextKey)) {
          nextStack = [...truncated, nextKey];
        }

        if (nextStack.length > 10) {
          nextStack = nextStack.slice(nextStack.length - 10);
        }

        const computedIndex = nextStack.findIndex((k) => String(k) === String(nextKey));
        const nextIndex = computedIndex >= 0 ? computedIndex : Math.max(0, nextStack.length - 1);

        navStackRef.current = nextStack;
        navIndexRef.current = nextIndex;
        setNavStack(nextStack);
        setNavIndex(nextIndex);
      }

      // Navigate.
      const payload = row?.ModelKey ? row : { ...(row || {}), ModelKey: nextKey, modelKey: nextKey };
      if (typeof onRequestYearChange === "function") {
        onRequestYearChange(payload);
      } else {
        setActiveModelKey(nextKey);
      }
    },
    [modelKey, onRequestYearChange]
  );

  const navigateToNavStackIndex = useCallback(
    (targetIndex) => {
      if (!Array.isArray(navStack) || navStack.length === 0) return;
      const clampIndex = (value) => Math.max(0, Math.min(value, navStack.length - 1));
      let idx = clampIndex(targetIndex);

      // If the stack contains consecutive duplicates (can happen depending on how the
      // model is opened/changed), skip over them so back/forward actually navigates.
      const direction = idx < navIndex ? -1 : 1;
      // Use the actually displayed key as the reference. During parent-driven
      // model changes, navIndex can briefly point at a different entry; using
      // displayedModelKey keeps Back/Forward symmetric and reliable.
      const currentKey = displayedModelKey ?? modelKey;
      while (idx >= 0 && idx < navStack.length && String(navStack[idx]) === String(currentKey)) {
        idx += direction;
      }
      if (idx < 0 || idx >= navStack.length) return;
      if (idx < 0 || idx >= navStack.length) return;

      const nextKey = navStack[idx];
      if (!nextKey) return;

      navActionRef.current = { type: "stack", targetIndex: idx, targetKey: nextKey, startedAt: Date.now() };
      navIndexRef.current = idx;
      setNavIndex(idx);

      const payload = { ModelKey: nextKey, modelKey: nextKey };
      if (typeof onRequestYearChange === "function") {
        onRequestYearChange(payload);
      } else {
        setActiveModelKey(nextKey);
      }
    },
    [navStack, displayedModelKey, modelKey, navIndex, onRequestYearChange]
  );

  const currentNavKey = useMemo(() => {
    return displayedModelKey ?? modelKey;
  }, [displayedModelKey, modelKey]);

  const backRecentTargetIndex = useMemo(() => {
    if (!Array.isArray(navStack) || navStack.length === 0) return null;
    if (navIndex <= 0) return null;
    for (let i = navIndex - 1; i >= 0; i--) {
      if (String(navStack[i]) !== String(currentNavKey)) return i;
    }
    return null;
  }, [navStack, navIndex, currentNavKey]);

  const forwardRecentTargetIndex = useMemo(() => {
    if (!Array.isArray(navStack) || navStack.length === 0) return null;
    if (navIndex < 0 || navIndex >= navStack.length - 1) return null;
    for (let i = navIndex + 1; i < navStack.length; i++) {
      if (String(navStack[i]) !== String(currentNavKey)) return i;
    }
    return null;
  }, [navStack, navIndex, currentNavKey]);

  const canGoBackRecent = backRecentTargetIndex != null;
  const canGoForwardRecent = forwardRecentTargetIndex != null;

  const recentDropdownValue = useMemo(() => {
    if (!recentModels.length) return "";
    const current = recentModels.find((e) => String(e.modelKey) === String(displayedModelKey));
    return current?.modelKey != null ? String(current.modelKey) : "";
  }, [recentModels, displayedModelKey]);

  useEffect(() => {
    const candidateKeys = [displayedModelKey, previousModelKey, nextModelKey].filter(Boolean);
    candidateKeys.forEach((key) => {
      if (!key) return;
      if (modelMetaMap[key] !== undefined) return;
      if (modelMetaRequestsRef.current.has(key)) return;
      modelMetaRequestsRef.current.add(key);
      axios
        .get(`/api/models/${key}`)
        .then((resp) => {
          const payload = resp?.data || null;
          setModelMetaMap((prev) => {
            if (prev[key] === payload) return prev;
            return { ...prev, [key]: payload };
          });
        })
        .catch((err) => {
          console.error("Failed to fetch model metadata", { key, err });
          setModelMetaMap((prev) => {
            if (prev[key] === null) return prev;
            return { ...prev, [key]: null };
          });
        })
        .finally(() => {
          modelMetaRequestsRef.current.delete(key);
        });
    });
  }, [displayedModelKey, previousModelKey, nextModelKey, modelMetaMap]);

  const currentModelLookup = displayedModelKey ? modelMetaMap[displayedModelKey] : null;
  const previousModelLookup = hasPreviousModel ? modelMetaMap[previousModelKey] : null;
  const nextModelLookup = hasNextModel ? modelMetaMap[nextModelKey] : null;

  const currentModelId =
    getMetaValue(currentMeta, ["ModelID", "modelId", "ModelId"]) ??
    currentModelLookup?.ModelID ??
    firstValueFromRows(currentRows, ["ModelID", "modelId", "ModelId"]);
  const currentYearId =
    getMetaValue(currentMeta, ["YearID", "yearId", "YearId"]) ??
    currentModelLookup?.YearID ??
    firstValueFromRows(currentRows, ["YearID", "yearId", "YearId"]);
  const previousModelId =
    getMetaValue(previousMeta, ["ModelID", "modelId", "ModelId"]) ??
    previousModelLookup?.ModelID ??
    firstValueFromRows(previousRows, ["ModelID", "modelId", "ModelId"]);
  const previousYearId =
    getMetaValue(previousMeta, ["YearID", "yearId", "YearId"]) ??
    previousModelLookup?.YearID ??
    firstValueFromRows(previousRows, ["YearID", "yearId", "YearId"]);
  const nextModelId =
    getMetaValue(nextMeta, ["ModelID", "modelId", "ModelId"]) ??
    nextModelLookup?.ModelID ??
    firstValueFromRows(nextRows, ["ModelID", "modelId", "ModelId"]);
  const nextYearId =
    getMetaValue(nextMeta, ["YearID", "yearId", "YearId"]) ??
    nextModelLookup?.YearID ??
    firstValueFromRows(nextRows, ["YearID", "yearId", "YearId"]);
  const parentProductGroupKey = currentMeta?.parentProductGroupKey ?? null;
  const subProductGroupKey = currentMeta?.subProductGroupKey ?? null;

  useEffect(() => {
    modelMetaMapRef.current = modelMetaMap;
  }, [modelMetaMap]);

  const refreshData = useCallback(() => setRefreshTick((t) => t + 1), []);

  const fetchModelDetail = useCallback(
    async (key) => {
      if (!key) return null;
      const cached = modelMetaMapRef.current[key];
      if (cached !== undefined) {
        return cached;
      }
      if (modelDetailRequestsRef.current.has(key)) {
        return modelDetailRequestsRef.current.get(key);
      }
      const request = axios
        .get(`/api/models/${key}`)
        .then((resp) => resp?.data || null)
        .catch((err) => {
          console.error("Failed to fetch model details", { key, err });
          return null;
        })
        .finally(() => {
          modelDetailRequestsRef.current.delete(key);
        });
      modelDetailRequestsRef.current.set(key, request);
      const result = await request;
      setModelMetaMap((prev) => {
        if (prev[key] === result) return prev;
        return { ...prev, [key]: result };
      });
      return result;
    },
    []
  );

  const fetchModelBlock = useCallback(
    async (key) => {
      if (!key) return null;
      if (modelBlockRequestsRef.current.has(key)) {
        return modelBlockRequestsRef.current.get(key);
      }
      const request = axios
        .get(`/api/data-collection-model/${key}`, { timeout: 60000 })
        .then((resp) => resp?.data?.current || null)
        .catch((err) => {
          console.error("Failed to load model block", { key, err });
          return null;
        })
        .finally(() => {
          modelBlockRequestsRef.current.delete(key);
        });
      modelBlockRequestsRef.current.set(key, request);
      const result = await request;
      const resolvedKey = result?.meta?.ModelKey ?? result?.meta?.modelKey ?? key;
      if (result?.meta && resolvedKey) {
        setModelMetaMap((prev) => {
          if (prev[resolvedKey]) return prev;
          return { ...prev, [resolvedKey]: result.meta };
        });
      }
      return result;
    },
    [setModelMetaMap]
  );

  const fetchMatchingNavOptions = useCallback(
    async (baseDescriptor, baseParams, options = {}) => {
      if (!baseDescriptor || !baseParams) {
        return [];
      }

      const { allowLegacyScanOnEmpty = false } = options;

      const normalizeList = (list) =>
        (Array.isArray(list) ? list : [])
          .map((candidate) => normalizeNavCandidate(candidate, baseDescriptor))
          .filter(Boolean);

      const runLegacyLookup = async () => {
        const deduped = [];
        const seenKeys = new Set();

        const appendOptions = (optionsList) => {
          optionsList.forEach((option) => {
            const key = option.ModelKey ?? `${option.ModelID || ''}-${option.YearID || ''}-${deduped.length}`;
            if (seenKeys.has(key)) return;
            seenKeys.add(key);
            deduped.push(option);
          });
        };

        const runQuery = async (params) => {
          const response = await axios.get('/api/models', { params });
          const rawCandidates = Array.isArray(response?.data) ? response.data : [];
          const normalized = rawCandidates
            .filter((candidate) => matchesModelBase(candidate, baseDescriptor))
            .map((candidate) => normalizeNavCandidate(candidate, baseDescriptor))
            .filter(Boolean);
          appendOptions(normalized);
          return rawCandidates.length;
        };

        let offset = 0;
        for (let page = 0; page < MODEL_LOOKUP_MAX_PAGES; page++) {
          const rowCount = await runQuery({ ...baseParams, limit: MODEL_LOOKUP_PAGE_SIZE, offset });
          if (deduped.length > 1 || rowCount < MODEL_LOOKUP_PAGE_SIZE) {
            break;
          }
          offset += MODEL_LOOKUP_PAGE_SIZE;
        }

        if (deduped.length <= 1) {
          try {
            await runQuery({ ...baseParams, limit: 0, offset: 0 });
          } catch (err) {
            console.error('Full model list lookup failed', err);
          }
        }

        const enriched = [];
        for (const option of deduped) {
          if (option.ModelKey) {
            const detail = await fetchModelDetail(option.ModelKey);
            if (detail) {
              enriched.push(normalizeNavCandidate(detail, baseDescriptor));
              continue;
            }
          }
          enriched.push(option);
        }
        return enriched;
      };

      let fastResults = [];
      try {
        const response = await axios.get('/api/models/nav-options', { params: baseParams });
        fastResults = normalizeList(response?.data);
        if (fastResults.length > 0 || !allowLegacyScanOnEmpty) {
          return fastResults;
        }
      } catch (err) {
        console.warn('Fast nav lookup failed, falling back to legacy scan.', err);
        return runLegacyLookup();
      }

      if (!allowLegacyScanOnEmpty) {
        return fastResults;
      }

      return runLegacyLookup();
    },
    [fetchModelDetail]
  );

  const collectNavOptions = useCallback(async (direction) => {
    const targetMeta =
      direction === "previous" ? previousMeta : direction === "next" ? nextMeta : null;
    if (!targetMeta) return null;

    const baseLabel = formatLabel(targetMeta, initialLabel);
    const targetDescriptor = buildDescriptorFromMeta(targetMeta);
    const targetParams = descriptorToParams(targetDescriptor);
    const targetModelKey = getMetaValue(targetMeta, ["modelKey", "ModelKey"]) ?? null;

    const currentDescriptor = buildDescriptorFromMeta(currentMeta);
    const currentParams = descriptorToParams(currentDescriptor);

    const targetOptionsRaw = targetParams
      ? await fetchMatchingNavOptions(targetDescriptor, targetParams, { allowLegacyScanOnEmpty: true })
      : [];

    let currentOptionsRaw = [];
    const shouldFetchCurrentAlternates = (!targetOptionsRaw || targetOptionsRaw.length === 0) && !!currentParams;
    if (shouldFetchCurrentAlternates) {
      currentOptionsRaw = await fetchMatchingNavOptions(currentDescriptor, currentParams);
    }

    const equals = (a, b) => {
      if (a === null || a === undefined || b === null || b === undefined) return false;
      return String(a) === String(b);
    };

    const filterCurrentCandidates = (options) => {
      if (!Array.isArray(options)) return [];
      return options.filter((option) => {
        const candidateKey = option.ModelKey ?? option.modelKey ?? null;
        if (candidateKey && displayedModelKey && equals(candidateKey, displayedModelKey)) {
          return false;
        }
        const candidateModelId = option.ModelID ?? option.modelId ?? option.ModelId ?? null;
        const candidateYearId = option.YearID ?? option.yearId ?? option.YearId ?? null;
        const sameComposite =
          !candidateKey &&
          candidateModelId !== null &&
          candidateYearId !== null &&
          currentModelId !== null &&
          currentYearId !== null &&
          equals(candidateModelId, currentModelId) &&
          equals(candidateYearId, currentYearId);
        return !sameComposite;
      });
    };

    const targetOptions = Array.isArray(targetOptionsRaw) ? targetOptionsRaw : [];
    const currentAlternates = filterCurrentCandidates(currentOptionsRaw);

    const combined = [];
    const seenKeys = new Set();
    const pushOptions = (options, originLabel, bucket) => {
      options.forEach((option, idx) => {
        const key = option.ModelKey ?? `${option.ModelID || ""}-${option.YearID || ""}-${bucket}-${idx}`;
        const isCurrentModel =
          (option.ModelKey ?? option.modelKey) && displayedModelKey
            ? equals(option.ModelKey ?? option.modelKey, displayedModelKey)
            : false;
        if (isCurrentModel) return;
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        combined.push({ ...option, __origin: originLabel });
      });
    };

    const originLabel = direction === "previous" ? "Previous Year" : "Next Year";
    pushOptions(targetOptions, originLabel, "target");
    if (currentAlternates.length > 0) {
      pushOptions(currentAlternates, "Current Year", "current");
    }

    return {
      direction,
      baseLabel,
      targetMeta,
      targetDescriptor,
      targetModelKey,
      options: combined,
      fallbackCandidate: combined[0] ?? normalizeNavCandidate(targetMeta, targetDescriptor)
    };
  }, [
    currentMeta,
    currentModelId,
    currentYearId,
    displayedModelKey,
    fetchMatchingNavOptions,
    initialLabel,
    nextMeta,
    previousMeta
  ]);

  const requestNavChoice = useCallback((payload) => {
    if (!payload) return Promise.reject(new Error("Missing navigation payload"));
    if (navChoiceResolverRef.current) {
      navChoiceResolverRef.current.reject(new Error("Navigation choice superseded"));
      navChoiceResolverRef.current = null;
    }
    return new Promise((resolve, reject) => {
      navChoiceResolverRef.current = { resolve, reject };
      setNavChoice(payload);
    });
  }, []);

  const resolveSourceBlock = useCallback(
    async (modelKey, fallbackBlock = null) => {
      if (!modelKey) return fallbackBlock ?? null;
      if (modelKey === displayedModelKey) {
        return { meta: currentMeta, rows: currentRows };
      }
      if (modelKey === previousModelKey && data?.previous) {
        return data.previous;
      }
      if (modelKey === nextModelKey && data?.next) {
        return data.next;
      }
      return fetchModelBlock(modelKey);
    },
    [currentMeta, currentRows, data, displayedModelKey, fetchModelBlock, nextModelKey, previousModelKey]
  );

  const handleGotoYear = useCallback(
    async (direction) => {
      const requestYearChange = (mergedMeta) => {
        if (typeof onRequestYearChange === "function") {
          onRequestYearChange(mergedMeta);
          return;
        }

        const nextKey = mergedMeta?.modelKey ?? mergedMeta?.ModelKey ?? null;
        if (!nextKey) {
          window.alert("No matching year found for this model.");
          return;
        }
        setActiveModelKey(nextKey);
      };

      setNavLookupDirection(direction);
      try {
        const navData = await collectNavOptions(direction);
        if (!navData) return;
        const { baseLabel, targetMeta, options, fallbackCandidate } = navData;

        const proceed = (candidate) => {
          const merged = mergeMetaWithCandidate(targetMeta, candidate, baseLabel);
          requestYearChange(merged);
        };

        if (!options || options.length <= 1) {
          proceed(options?.[0] ?? fallbackCandidate ?? null);
          return;
        }

        const { candidate } = await requestNavChoice({
          mode: "navigate",
          direction,
          baseLabel,
          options
        });
        proceed(candidate || fallbackCandidate || null);
      } catch (err) {
        if (err?.message !== "Navigation choice superseded" && err?.message !== "Navigation choice cancelled") {
          console.error("Failed to resolve navigation options", err);
        }
      } finally {
        setNavLookupDirection("");
      }
    },
    [collectNavOptions, onRequestYearChange, requestNavChoice]
  );

  const handleNavChoiceCancel = useCallback(() => {
    if (navChoiceResolverRef.current) {
      navChoiceResolverRef.current.reject(new Error("Navigation choice cancelled"));
      navChoiceResolverRef.current = null;
    }
    setNavChoice(null);
  }, []);

  const handleNavChoiceSelect = useCallback(
    (candidate) => {
      if (!candidate) {
        handleNavChoiceCancel();
        return;
      }
      if (navChoiceResolverRef.current) {
        navChoiceResolverRef.current.resolve({ candidate, payload: navChoice });
        navChoiceResolverRef.current = null;
      } else {
        console.warn("Navigation choice selected without pending resolver", candidate);
      }
      setNavChoice(null);
    },
    [handleNavChoiceCancel, navChoice]
  );

  function closeOnBackdrop(e) {
    if (e.target === e.currentTarget && typeof onClose === "function") onClose();
  }

  // Applies sibling copy updates in the correct order so dependent fields wait for the FitmentKey.
  const runSequentialCopy = useCallback(
    async (identity, noteMarker, updatesInput) => {
      if (!identity) {
        throw new Error("Missing target identity for copy operation.");
      }

      const queue = Array.isArray(updatesInput) ? updatesInput.filter(Boolean) : [];
      if (queue.length === 0) return;

      const updateOrder = ["OEMNumber", "Quantity", "OEMNote", "AdditionalFitmentInfo", "FicheURL"];
      const sorted = queue
        .slice()
        .sort((a, b) => updateOrder.indexOf(a.field) - updateOrder.indexOf(b.field));
      let activeIdentity = identity;

      const getLatestRow = () => {
        const rows = rowsStateRef.current || [];
        const idx = findRowIndexByIdentity(rows, activeIdentity);
        if (idx >= 0) {
          return rows[idx];
        }

        if (typeof activeIdentity === "string" && activeIdentity.startsWith("tmp:")) {
          const tempId = activeIdentity.slice(4);
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const rowTempId = row?.__tempId ?? row?._tempId ?? row?.TempId ?? row?.tempId;
            if (rowTempId !== null && rowTempId !== undefined && String(rowTempId) === tempId) {
              return row;
            }
          }
        }

        return null;
      };

      const waitForFitment = async () => {
        for (let attempt = 0; attempt < 12; attempt++) {
          const latestRow = getLatestRow();
          if (latestRow?.FitmentKey) return latestRow;
          await new Promise((resolve) => setTimeout(resolve, 75));
        }
        return getLatestRow();
      };

      const applyLocal = (field, value) => {
        setRowsState((prev) => {
          const idx = findRowIndexByIdentity(prev, activeIdentity);
          if (idx < 0) return prev;
          const next = prev.slice();
          next[idx] = {
            ...next[idx],
            Note: noteMarker,
            IsGhost: false,
            isGhost: false,
            IsGhostOneOff: false,
            isGhostOneOff: false,
            [field]: value
          };
          rowsStateRef.current = next;
          return next;
        });
      };

      const waitForInitialRow = async () => {
        const maxAttempts = 12;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          const candidate = getLatestRow();
          if (candidate) return candidate;
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        return getLatestRow();
      };

      let latest = getLatestRow();
      if (!latest) {
        latest = await waitForInitialRow();
      }
      if (!latest) {
        throw new Error("Target row could not be found.");
      }

      const needsCreation = !latest.FitmentKey;
      const includesOEM = sorted.some((u) => u.field === "OEMNumber");
      if (needsCreation && !includesOEM) {
        throw new Error("Target fitment has no record yet—copy an OEM Number first.");
      }

      for (const { field, value } of sorted) {
        if (field === "OEMNumber") {
          applyLocal(field, value);
          await commitEdit(activeIdentity, field, value, { deferResetEditing: true });
          latest = await waitForFitment();
          if (latest?.FitmentKey) {
            activeIdentity = rowIdentityKey(latest);
          }
          continue;
        }

        latest = getLatestRow();
        if (!latest?.FitmentKey) {
          latest = await waitForFitment();
          if (!latest?.FitmentKey) {
            throw new Error("Fitment record missing after OEM copy.");
          }
          activeIdentity = rowIdentityKey(latest);
        }

        applyLocal(field, value);
        await commitEdit(activeIdentity, field, value, {
          deferResetEditing: true,
          forceFitmentKey: latest.FitmentKey
        });
      }
    },
    [commitEdit]
  );

  const askCopyResolution = useCallback((message, options = {}) => {
    const allowOverride = options.allowOverride !== false;
    const promptSuffix = allowOverride
      ? "\nType O to overwrite the existing row, A to add a new row, or I to ignore."
      : "\nType A to add a new row, or I to ignore.";
    const defaultChoice = allowOverride ? "O" : "A";
    const response = window.prompt(`${message}${promptSuffix}`, defaultChoice);
    if (response === null) return "ignore";
    const normalized = response.trim().toLowerCase();
    if (normalized.startsWith("a")) return "add";
    if (normalized.startsWith("i")) return "ignore";
    if (allowOverride && normalized.startsWith("o")) return "override";
    return allowOverride ? "override" : "ignore";
  }, []);

  const createAdditionalCurrentRow = useCallback(
    (seedRow, noteMarker = "") => {
      const base = seedRow || {};
      const modelKeyForRow = displayedModelKey ?? base.ModelKey ?? base.modelKey ?? null;
      const itemTypeKey = base.ItemTypeKey ?? base.itemTypeKey ?? null;
      const fitmentLocationKey = base.FitmentLocationKey ?? base.fitmentLocationKey ?? null;
      if (
        modelKeyForRow === null ||
        itemTypeKey === null ||
        itemTypeKey === undefined ||
        fitmentLocationKey === null ||
        fitmentLocationKey === undefined
      ) {
        console.warn("Unable to create additional row due to missing keys.", {
          modelKeyForRow,
          itemTypeKey,
          fitmentLocationKey
        });
        return null;
      }

      tempRowCounterRef.current += 1;
      const tempId = `copy-${Date.now()}-${tempRowCounterRef.current}`;

      const duplicate = {
        ...base,
        ModelKey: modelKeyForRow,
        modelKey: modelKeyForRow,
        ItemTypeKey: itemTypeKey,
        FitmentLocationKey: fitmentLocationKey,
        FitmentLocation:
          base.FitmentLocation ?? base.location ?? base.Location ?? base.fitmentLocation ?? "",
        ItemType: base.ItemType ?? base.itemType ?? "",
        ItemTypeSort: base.ItemTypeSort ?? base.itemTypeSort ?? null,
        Make: base.Make ?? (currentMeta && currentMeta.make) ?? "",
        ModelName: base.ModelName ?? (currentMeta && currentMeta.modelName) ?? "",
        StreetName: base.StreetName ?? (currentMeta && currentMeta.streetName) ?? "",
        Year:
          currentMeta && currentMeta.year !== undefined
            ? currentMeta.year
            : base.Year ?? base.year ?? null,
        Region: base.Region ?? (currentMeta && currentMeta.region) ?? null,
        OEMNumber: "",
        Quantity: null,
        OEMNote: "",
        AdditionalFitmentInfo: "",
        FicheURL: "",
        SKUGroup: "",
        SKU: "",
        SupplierPartNumber: "",
        DateCreated: null,
        DateLastUpdated: null,
        RecentFields: "",
        Note: noteMarker,
        FitmentKey: null,
        fitmentKey: null,
        OEMKey: null,
        IsGhost: true,
        isGhost: true,
        IsGhostOneOff: false,
        isGhostOneOff: false,
        IsOneOff: base.IsOneOff ?? base.isOneOff ?? false,
        isOneOff: base.isOneOff ?? base.IsOneOff ?? false,
        OneOffGroupKey: base.OneOffGroupKey ?? base.oneOffGroupKey ?? null,
        ModelSortOrder: base.ModelSortOrder ?? base.modelSortOrder ?? 0,
        __tempId: tempId,
        _tempId: tempId,
        SourceTag: base.SourceTag ? `${base.SourceTag}-copy` : "copy-duplicate",
        _section: "current",
        IsCurrent: true
      };

      setRowsState((prev) => {
        const next = [...prev, duplicate];
        next.sort(compareRows);
        rowsStateRef.current = next;
        return next;
      });

      return duplicate;
    },
    [currentMeta, displayedModelKey]
  );

  // Copy from previous/next into current
  const copyFromRow = useCallback(
    async function (sourceRow) {
      if (!canWrite || !sourceRow) return;

      const relation = sourceRow._section || (sourceRow.IsCurrent ? "current" : "sibling");
      const relationLabel = relation === "previous" ? "previous year" : relation === "next" ? "next year" : "source";
      const noteMarker = relation === "previous" ? "PREV" : relation === "next" ? "NEXT" : "COPY";
      const sourceKey = relation + "-" + sourceRow.FitmentLocationKey + "-" + sourceRow.ItemTypeKey;

      const { updates, hasData, hasOEM } = extractCopyUpdates(sourceRow);
      if (!hasData) {
        window.alert("Source row has no data to copy.");
        return;
      }

      const currentRowsSnapshot = rowsStateRef.current || [];
      const candidateRows = currentRowsSnapshot.filter(
        (row) =>
          row.IsCurrent &&
          row.FitmentLocationKey === sourceRow.FitmentLocationKey &&
          row.ItemTypeKey === sourceRow.ItemTypeKey
      );

      let targetRow = null;

      if (candidateRows.length > 0) {
        targetRow =
          candidateRows.find((row) => rowIdentityKey(row) === selectedCell.rowIdentity) ||
          candidateRows.find((row) => !rowHasMeaningfulData(row)) ||
          candidateRows[0];
      } else {
        if (!hasOEM) {
          window.alert("Unable to add a new row because the source row has no OEM Number.");
          return;
        }
        const seeded = {
          ...sourceRow,
          _section: "current",
          IsCurrent: true
        };
        targetRow = createAdditionalCurrentRow(seeded, noteMarker);
        if (!targetRow) {
          window.alert("Unable to create a new row for this copy.");
          return;
        }
      }

      const hasExisting = updates.some(({ field }) => {
        const currentVal = targetRow ? targetRow[field] : null;
        return field === "Quantity"
          ? currentVal !== null && currentVal !== undefined
          : normalizeCopyField(currentVal).length > 0;
      });

      if (hasExisting) {
        const conflictLines = updates
          .filter(({ field }) => {
            const currentVal = targetRow[field];
            return field === "Quantity"
              ? currentVal !== null && currentVal !== undefined
              : normalizeCopyField(currentVal).length > 0;
          })
          .map(({ field, value }) => {
            const currentVal = displayValue(field, targetRow[field]) || "—";
            const sourceVal = displayValue(field, value) || "—";
            return `${field}: Current "${currentVal}" vs ${relationLabel} "${sourceVal}"`;
          });
        const itemTypeLabel = sourceRow.ItemType || targetRow.ItemType || "";
        const locationLabel = sourceRow.FitmentLocation || targetRow.FitmentLocation || "";
        const messageBase = `Data already exists for ${itemTypeLabel || "this Item Type"} / ${locationLabel || "this Fitment Location"}.`;
        const message = conflictLines.length > 0 ? `${messageBase}\n${conflictLines.join("\n")}` : messageBase;

        const decision = askCopyResolution(message);
        if (decision === "ignore") return;
        if (decision === "add") {
          if (!hasOEM) {
            window.alert("Cannot add a new row because the source row has no OEM Number.");
            return;
          }
          const duplicate = createAdditionalCurrentRow(targetRow, noteMarker);
          if (!duplicate) {
            window.alert("Unable to create an additional row for this copy.");
            return;
          }
          targetRow = duplicate;
        }
      }

      const targetIdentity = rowIdentityKey(targetRow);
      if (!targetIdentity) {
        window.alert("Unable to determine the target row for copying.");
        return;
      }

      try {
        setCopyingKey(sourceKey);
        await runSequentialCopy(targetIdentity, noteMarker, updates);
        setEditing({ rowIdentity: "", field: "", value: "" });
      } catch (err) {
        console.error("Copy from sibling row failed:", err);
        const friendlyMessage = err?.response?.data?.error || err?.message || "Copy failed. Please try again.";
        window.alert(friendlyMessage);
      } finally {
        setCopyingKey("");
        await refreshData();
      }
    },
    [askCopyResolution, canWrite, createAdditionalCurrentRow, refreshData, runSequentialCopy, selectedCell.rowIdentity]
  );
  useEffect(() => {
    rowsStateRef.current = rowsState;
  }, [rowsState]);

  useEffect(() => {
  const combined = [];

    if (showPrevNext && previousRows.length > 0) {
      for (let i = 0; i < previousRows.length; i++) {
        const rPrev = previousRows[i];
        if (rPrev?.IsGhost || rPrev?.isGhost) continue;
        if (!hasSiblingRowData(rPrev)) continue;
        combined.push({
          ...rPrev,
          Make: rPrev.Make ?? (previousMeta && previousMeta.make),
          ModelName: rPrev.ModelName ?? (previousMeta && previousMeta.modelName),
          Region: rPrev.Region ?? (previousMeta && previousMeta.region),
          Year: previousMeta && previousMeta.year !== undefined ? previousMeta.year : null,
          _section: "previous",
          IsCurrent: false
        });
      }
    }

    for (let j = 0; j < currentRows.length; j++) {
      const rCur = currentRows[j];
      combined.push({
        ...rCur,
        Make: rCur.Make ?? (currentMeta && currentMeta.make),
        ModelName: rCur.ModelName ?? (currentMeta && currentMeta.modelName),
        Region: rCur.Region ?? (currentMeta && currentMeta.region),
        Year: currentMeta && currentMeta.year !== undefined ? currentMeta.year : null,
        _section: "current",
        IsCurrent: true
      });
    }

    if (showPrevNext && nextRows.length > 0) {
      for (let k = 0; k < nextRows.length; k++) {
        const rNext = nextRows[k];
        if (rNext?.IsGhost || rNext?.isGhost) continue;
        if (!hasSiblingRowData(rNext)) continue;
        combined.push({
          ...rNext,
          Make: rNext.Make ?? (nextMeta && nextMeta.make),
          ModelName: rNext.ModelName ?? (nextMeta && nextMeta.modelName),
          Region: rNext.Region ?? (nextMeta && nextMeta.region),
          Year: nextMeta && nextMeta.year !== undefined ? nextMeta.year : null,
          _section: "next",
          IsCurrent: false
        });
      }
    }
    const dedupedByKey = new Map();
    for (let idx = 0; idx < combined.length; idx++) {
      const row = combined[idx];
      const fitKey = row.FitmentKey ?? row.fitmentKey;
      const tempId = row.__tempId ?? row._tempId ?? row.TempId ?? row.tempId;
      const key = fitKey !== null && fitKey !== undefined
        ? `fit:${fitKey}:${row._section}`
        : tempId !== null && tempId !== undefined && String(tempId).length > 0
        ? `tmp:${tempId}:${row._section}`
        : `${row.ModelKey}-${row.FitmentLocationKey}-${row.ItemTypeKey}-${row._section}`;
      const existing = dedupedByKey.get(key);
      dedupedByKey.set(key, pickPreferredRow(existing, row));
    }

    const sortedCombined = Array.from(dedupedByKey.values());
    sortedCombined.sort(compareRows);

    const existingRows = rowsStateRef.current || [];
    const existingMap = new Map();
    for (let idx = 0; idx < existingRows.length; idx++) {
      const existingRow = existingRows[idx];
      existingMap.set(rowIdentityKey(existingRow), existingRow);
    }

    const merged = sortedCombined.map((row) => {
      const key = rowIdentityKey(row);
      const existing = existingMap.get(key);
      if (!existing) return row;
      return { ...row, ...existing };
    });

    const presentKeys = new Set(merged.map((row) => rowIdentityKey(row)));
    const prevNextVisible = !!showPrevNext;
    for (let idx = 0; idx < existingRows.length; idx++) {
      const existingRow = existingRows[idx];
      const key = rowIdentityKey(existingRow);
      if (presentKeys.has(key)) continue;

      const section = existingRow._section || (existingRow.IsCurrent ? "current" : "sibling");
      const shouldKeep = section === "current" || prevNextVisible;
      if (shouldKeep) {
        merged.push(existingRow);
      }
    }

    merged.sort(compareRows);

    const combosWithSavedRows = new Set();
    for (let idx = 0; idx < merged.length; idx++) {
      const row = merged[idx];
      if (!row?.IsCurrent) continue;
      const fitKey = row.FitmentKey ?? row.fitmentKey;
      if (fitKey === null || fitKey === undefined) continue;
      if (!rowHasMeaningfulData(row)) continue;
      combosWithSavedRows.add(comboKey(row));
    }

    const cleaned = merged.filter((row) => {
      if (!row?.IsCurrent) return true;
      if (!isGhostRow(row)) return true;
      const fitKey = row.FitmentKey ?? row.fitmentKey;
      if (fitKey !== null && fitKey !== undefined) return true;
      return !combosWithSavedRows.has(comboKey(row));
    });

    setRowsState(cleaned);
    rowsStateRef.current = cleaned;
  }, [data, showPrevNext, refreshTick, currentMeta, previousMeta, nextMeta, currentRows, previousRows, nextRows]);

  const completedCounts = useMemo(() => {
    const counts = { previous: 0, current: 0, next: 0 };
    rowsState.forEach((row) => {
      const hasOEM = normalizeCopyField(row.OEMNumber).length > 0;
      if (!hasOEM) return;
      const section = row._section || (row.IsCurrent ? "current" : "current");
      if (counts[section] === undefined) counts[section] = 0;
      counts[section] += 1;
    });
    return counts;
  }, [rowsState]);

  const rowsAfterHide = useMemo(() => {
    if (!hideCompleted) return rowsState;
    const completedPairs = new Set();
    rowsState.forEach((row) => {
      if (!row.IsCurrent) return;
      const hasOEM = normalizeCopyField(row.OEMNumber).length > 0;
      if (!hasOEM) return;
      
      // Get cached OEMType for this OEM number (case-insensitive check)
      const oemNumber = normalizeCopyField(row.OEMNumber);
      const cachedOEMType = oemTypeCacheRef.current.get(oemNumber) || null;
      
      // Skip "__UNKNOWN__" marker - treat as unknown type requiring both fields
      const oemTypeLower = (cachedOEMType && cachedOEMType !== "__UNKNOWN__") 
        ? cachedOEMType.toLowerCase() 
        : null;
      
      // For Misc type: completed if has OEMNumber (regardless of quantity)
      if (oemTypeLower === "misc") {
        completedPairs.add(comboKey(row));
        return;
      }
      
      // For OEM, NonOEM, Supplier types or unknown (null): 
      // completed only if BOTH OEMNumber AND Quantity have values
      const hasQuantity = row.Quantity !== null && row.Quantity !== undefined && row.Quantity !== "";
      if (hasOEM && hasQuantity) {
        completedPairs.add(comboKey(row));
      }
    });
    if (completedPairs.size === 0) return rowsState;
    return rowsState.filter((row) => !completedPairs.has(comboKey(row)));
  }, [rowsState, hideCompleted, oemTypeCacheTick]);

  // When rows get hidden (e.g., via hideCompleted), move focus to next visible row if current is hidden
  useEffect(() => {
    if (!selectedCell.rowIdentity) return;
    const isVisibleInFiltered = rowsAfterHide.some(
      (row) => rowIdentityKey(row) === selectedCell.rowIdentity
    );
    if (isVisibleInFiltered) return;

    // Current row is hidden, find the next visible "current" section row
    const currentIndex = rowsState.findIndex(
      (row) => rowIdentityKey(row) === selectedCell.rowIdentity
    );
    if (currentIndex < 0) return;

    // Look for next visible row below
    for (let idx = currentIndex + 1; idx < rowsState.length; idx++) {
      const candidate = rowsState[idx];
      if (!candidate) continue;
      const section = candidate._section || (candidate.IsCurrent ? "current" : "sibling");
      if (section !== "current") continue;
      const candidateKey = rowIdentityKey(candidate);
      if (rowsAfterHide.some((r) => rowIdentityKey(r) === candidateKey)) {
        setSelectedCell({ rowIdentity: candidateKey, field: "OEMNumber" });
        scheduleFocusEditableCell(candidateKey, "OEMNumber");
        return;
      }
    }

    // If no row below, look above
    for (let idx = currentIndex - 1; idx >= 0; idx--) {
      const candidate = rowsState[idx];
      if (!candidate) continue;
      const section = candidate._section || (candidate.IsCurrent ? "current" : "sibling");
      if (section !== "current") continue;
      const candidateKey = rowIdentityKey(candidate);
      if (rowsAfterHide.some((r) => rowIdentityKey(r) === candidateKey)) {
        setSelectedCell({ rowIdentity: candidateKey, field: "OEMNumber" });
        scheduleFocusEditableCell(candidateKey, "OEMNumber");
        return;
      }
    }

    // No visible rows left, clear selection
    setSelectedCell({ rowIdentity: "", field: "" });
  }, [rowsAfterHide, selectedCell.rowIdentity, rowsState, scheduleFocusEditableCell]);

  useEffect(() => {
    if (!selectedCell.rowIdentity) return;
    const exists = rowsState.some((row) => rowIdentityKey(row) === selectedCell.rowIdentity);
    if (!exists) {
      setSelectedCell({ rowIdentity: "", field: "" });
    }
  }, [rowsState, selectedCell.rowIdentity]);

  const selectedRow = useMemo(() => {
    if (!selectedCell.rowIdentity) return null;
    return rowsState.find((row) => rowIdentityKey(row) === selectedCell.rowIdentity) || null;
  }, [rowsState, selectedCell.rowIdentity]);

  const completedSummaryEntries = useMemo(() => {
    const baseOrder = ["previous", "current", "next"];
    const activeSection = selectedRow?._section || (selectedRow?.IsCurrent ? "current" : "current");
    const allowedKeys = baseOrder.filter((key) => key === "current" || showPrevNext);
    const entries = allowedKeys.map((key) => ({
      key,
      label: key === "previous" ? "Prev" : key === "current" ? "Current" : "Next",
      value: completedCounts[key] || 0,
      isActive: key === activeSection
    }));
    entries.sort((a, b) => {
      if (a.isActive === b.isActive) {
        return baseOrder.indexOf(a.key) - baseOrder.indexOf(b.key);
      }
      return a.isActive ? -1 : 1;
    });
    return entries;
  }, [completedCounts, showPrevNext, selectedRow]);

  const selectedManageRow = useMemo(() => {
    if (!selectedRow) return null;
    if (selectedRow.IsCurrent) return selectedRow;
    return (
      rowsState.find(
        (row) =>
          row.IsCurrent &&
          row.FitmentLocationKey === selectedRow.FitmentLocationKey &&
          row.ItemTypeKey === selectedRow.ItemTypeKey
      ) || null
    );
  }, [rowsState, selectedRow]);

  const handleSelectRow = useCallback((rowIdentity) => {
    setSelectedCell((prev) => {
      if (prev.rowIdentity === rowIdentity) return prev;
      return { rowIdentity, field: "" };
    });
  }, []);

  const handleSelectCell = useCallback((rowIdentity, field) => {
    setSelectedCell({ rowIdentity, field });
  }, []);

  const handleOpenManageOEM = useCallback(() => {
    if (!canWrite) {
      window.alert("You do not have permission to manage OEM data.");
      return;
    }
    const row = selectedManageRow;
    if (!row) {
      window.alert("Select a current-year line before opening the OEM manager.");
      return;
    }
    setManageInitialOEMNumber(normalizeCopyField(row.OEMNumber));
    setManageInitialOEMKey(row.OEMKey ?? null);
    setManageOEMOpen(true);
  }, [canWrite, selectedManageRow]);

  const handleOpenManageSKU = useCallback(() => {
    if (!canWrite) {
      window.alert("You do not have permission to manage SKU groups.");
      return;
    }
    const row = selectedManageRow;
    if (!row) {
      window.alert("Select a current-year line before opening the SKU manager.");
      return;
    }
    setManageInitialSKUGroup(normalizeCopyField(row.SKUGroup));
    setManageSKUOpen(true);
  }, [canWrite, selectedManageRow]);

  const extractKitId = (entity) => {
    if (!entity) return null;
    const direct = entity.KitId ?? entity.KitID ?? entity.LinkedKitId;
    if (direct) return direct;
    const oem = entity.OEMNumber;
    if (typeof oem === "string" && oem.toUpperCase().startsWith(KIT_ID_PREFIX)) {
      return oem;
    }
    return null;
  };

  const updateRowWithFitment = (fitmentKey, updates) => {
    if (!fitmentKey) return;
    setRowsState((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (row.FitmentKey !== fitmentKey) return row;
        changed = true;
        return { ...row, ...updates };
      });
      if (changed) {
        rowsStateRef.current = next;
        return next;
      }
      return prev;
    });
  };

  const refreshFitment = async (fitmentKey) => {
    if (!fitmentKey) return null;
    try {
      const { data } = await axios.get(`/api/fitments/${fitmentKey}`);
      if (data) {
        updateRowWithFitment(fitmentKey, data);
      }
      return data;
    } catch (err) {
  console.error("Failed to refresh fitment for kits:", err);
      return null;
    }
  };

  const closeKitModal = () => {
    const fitmentKey = kitModalState.fitment?.FitmentKey ?? null;
    setKitModalState({ open: false, kitId: null, fitment: null });
    if (fitmentKey) {
      refreshFitment(fitmentKey);
    }
  };

  const ensureFitmentForKit = async (context) => {
    if (!context) return null;
    if (context.FitmentKey) {
      return { fitmentKey: context.FitmentKey, rowIdentity: context.rowIdentity ?? null };
    }

    const rows = rowsStateRef.current || [];
    const identity = context.rowIdentity ?? '';
    const idx = identity ? findRowIndexByIdentity(rows, identity) : -1;
    const sourceRow = idx >= 0 ? rows[idx] : null;

    if (!sourceRow || sourceRow.ModelKey == null || sourceRow.FitmentLocationKey == null || sourceRow.ItemTypeKey == null) {
      window.alert("Cannot create a kit row without model, item type, and fitment location information.");
      return null;
    }

    const effectiveIdentity = identity || rowIdentityKey(sourceRow);

    try {
      const { data } = await axios.post('/api/fitments', {
        modelKey: sourceRow.ModelKey,
        fitmentLocationKey: sourceRow.FitmentLocationKey,
        itemTypeKey: sourceRow.ItemTypeKey,
        field: 'OEMNumber',
        value: null
      });

      const fitment = data?.updated;
      if (fitment?.FitmentKey) {
        setRowsState(prev => {
          const next = prev.slice();
          const targetIdx = findRowIndexByIdentity(next, effectiveIdentity);
          if (targetIdx >= 0) {
            next[targetIdx] = {
              ...next[targetIdx],
              ...fitment,
              FitmentKey: fitment.FitmentKey,
              IsGhost: false,
              isGhost: false,
              IsGhostOneOff: false,
              isGhostOneOff: false
            };
          }
          rowsStateRef.current = next;
          return next;
        });

        setKitModalState(prev => {
          if (!prev.open) return prev;
          const nextFitment = prev.fitment
            ? { ...prev.fitment, FitmentKey: fitment.FitmentKey, rowIdentity: effectiveIdentity }
            : prev.fitment;
          return { ...prev, fitment: nextFitment };
        });

        return { fitmentKey: fitment.FitmentKey, rowIdentity: effectiveIdentity };
      }
    } catch (err) {
      console.error('Failed to create fitment for kit:', err);
      window.alert('Failed to create a fitment row for this kit. See console for details.');
    }

    return null;
  };

  const openKitModalForRow = async (row) => {
    if (!canManageKits) {
      window.alert('You do not have permission to manage kits.');
      return;
    }

    const identity = rowIdentityKey(row);
    const rows = rowsStateRef.current || [];
    const idx = findRowIndexByIdentity(rows, identity);
    const resolvedRow = idx >= 0 ? rows[idx] : row;
    let fitmentKey = resolvedRow?.FitmentKey ?? null;
    let latest = null;

    if (fitmentKey) {
      latest = await refreshFitment(fitmentKey);
      fitmentKey = latest?.FitmentKey ?? fitmentKey;
    }

    const kitId = extractKitId(latest) ?? extractKitId(resolvedRow) ?? null;
    const fitmentContext = {
      FitmentKey: fitmentKey,
      ItemTypeKey: resolvedRow?.ItemTypeKey ?? null,
      ItemType: resolvedRow?.ItemType ?? '',
      FitmentLocationKey: resolvedRow?.FitmentLocationKey ?? null,
      FitmentLocation: resolvedRow?.FitmentLocation ?? '',
      OEMNumber: resolvedRow?.OEMNumber ?? '',
      SKUGroup: resolvedRow?.SKUGroup ?? '',
      ModelKey: resolvedRow?.ModelKey ?? displayedModelKey ?? modelKey ?? null,
      Make: resolvedRow?.Make ?? currentMeta?.make ?? '',
      ModelName: resolvedRow?.ModelName ?? currentMeta?.modelName ?? '',
      Year: resolvedRow?.Year ?? currentMeta?.year ?? null,
      Region: resolvedRow?.Region ?? currentMeta?.region ?? null,
      Quantity: resolvedRow?.Quantity ?? null,
      OEMNote: resolvedRow?.OEMNote ?? '',
      rowIdentity: identity
    };

    setKitModalState({ open: true, kitId, fitment: fitmentContext });
  };

  const handleKitLinked = async (kitId, meta = {}) => {
    if (!kitId) return;

    const fitmentKey = meta.fitmentKey ?? kitModalState.fitment?.FitmentKey ?? null;
    if (!fitmentKey) return;

    const identity =
      meta.ensureResult?.rowIdentity ||
      kitModalState.fitment?.rowIdentity ||
      (() => {
        const rows = rowsStateRef.current || [];
        const match = rows.find(r => r.FitmentKey === fitmentKey);
        return match ? rowIdentityKey(match) : '';
      })();

    if (!identity) return;

    try {
      await commitEdit(identity, 'OEMNumber', kitId, { allowDuringCreate: true, forceFitmentKey: fitmentKey });

      const latestRows = rowsStateRef.current || [];
      const idx = findRowIndexByIdentity(latestRows, identity);
      const latestRow = idx >= 0 ? latestRows[idx] : null;

      const qtyRaw = latestRow?.Quantity;
      const parsedQty = Number(qtyRaw);
      const quantityValue = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;
      await commitEdit(identity, 'Quantity', quantityValue, { allowDuringCreate: true, forceFitmentKey: fitmentKey });

      const noteRaw = (latestRow?.OEMNote ?? '').toString().trim();
      // The kit's OEM note should list every component OEM number and quantity
      // (e.g. "12345-ABC x2, 67890-DEF x1"), not just the kit number itself.
      let composedKitNote = '';
      try {
        const kitResp = await axios.get(`/api/kits/${encodeURIComponent(kitId)}`);
        composedKitNote = buildKitOEMNote(kitResp?.data?.components);
      } catch (kitErr) {
        console.warn('Failed to load kit components for OEM note', kitErr);
      }
      const noteValue = composedKitNote || (noteRaw.length > 0 ? noteRaw : `Kit ${kitId}`);
      await commitEdit(identity, 'OEMNote', noteValue, { allowDuringCreate: true, forceFitmentKey: fitmentKey });

      updateRowWithFitment(fitmentKey, {
        FitmentKey: fitmentKey,
        OEMNumber: kitId,
        Quantity: quantityValue,
        OEMNote: noteValue,
        KitId: kitId,
        KitID: kitId,
        IsKit: true,
        IsGhost: false,
        isGhost: false,
        IsGhostOneOff: false,
        isGhostOneOff: false
      });

      await refreshFitment(fitmentKey);

      setSelectedKitRowIds(new Set());

      setKitModalState(prev => {
        if (!prev.open) return prev;
        const nextFitment = prev.fitment
          ? { ...prev.fitment, FitmentKey: fitmentKey, rowIdentity: identity }
          : prev.fitment;
        return { ...prev, kitId, fitment: nextFitment };
      });
    } catch (err) {
      console.error('Failed to sync kit fitment details:', err);
      window.alert('Kit saved but failed to update the fitment row. See console for details.');
    }
  };

  const handleOpenAddOneOff = useCallback(() => {
    if (!canWrite) {
      alert("You do not have permission to add one-off rows.");
      return;
    }
    const baseRow = selectedManageRow;
    if (!baseRow) {
      alert("Select a current-year line before adding a one-off row.");
      return;
    }
    const resolvedModelKey = baseRow.ModelKey ?? displayedModelKey ?? modelKey ?? null;
    if (!resolvedModelKey) {
      alert("Unable to determine the model key for the one-off row.");
      return;
    }

    const existingKeys = (rowsStateRef.current || [])
      .filter(
        (row) =>
          row.IsCurrent &&
          (row.ModelKey ?? resolvedModelKey) === resolvedModelKey &&
          row.ItemTypeKey === baseRow.ItemTypeKey &&
          row.FitmentLocationKey != null
      )
      .map((row) => row.FitmentLocationKey);

    const uniqueKeys = Array.from(new Set(existingKeys));

    setOneOffContext({
      baseRow,
      productGroupKey: parentProductGroupKey,
      subProductGroupKey,
      modelKey: resolvedModelKey,
      existingKeys: uniqueKeys
    });
  }, [canWrite, selectedManageRow, parentProductGroupKey, subProductGroupKey, displayedModelKey, modelKey]);

  const addGhostOneOffRow = useCallback(
    (sourceRow, modelCtx = {}, itemCtx = {}, locationCtx = {}) => {
      const resolvedModelKey = modelCtx.ModelKey ?? displayedModelKey ?? modelKey ?? null;
      const resolvedItemTypeKey = itemCtx.ItemTypeKey ?? null;
      const resolvedLocationKey =
        locationCtx.FitmentLocationKey ?? locationCtx.fitmentLocationKey ?? null;
      if (!resolvedModelKey || !resolvedItemTypeKey || resolvedLocationKey == null) {
        return;
      }

      const baseRow = sourceRow || {};
      const resolvedItemTypeSort =
        itemCtx.ItemTypeSort ??
        itemCtx.SortOrder ??
        baseRow.ItemTypeSort ??
        baseRow.itemTypeSort ??
        null;
      const rawLocationSort =
        locationCtx.SortOrder ?? locationCtx.sortOrder ?? locationCtx.LocationSort ?? null;
      const locationSort =
        rawLocationSort !== null && rawLocationSort !== undefined
          ? Number(rawLocationSort)
          : null;
      const ghostRow = {
        ...baseRow,
        ModelKey: resolvedModelKey,
        Make: modelCtx.Make ?? baseRow.Make ?? (currentMeta && currentMeta.make) ?? "",
        ModelName: modelCtx.ModelName ?? baseRow.ModelName ?? (currentMeta && currentMeta.modelName) ?? "",
        StreetName:
          modelCtx.StreetName ?? baseRow.StreetName ?? (currentMeta && currentMeta.streetName) ?? "",
        Year:
          modelCtx.Year ??
          baseRow.Year ??
          (currentMeta && currentMeta.year !== undefined ? currentMeta.year : null),
        Region: modelCtx.Region ?? baseRow.Region ?? (currentMeta && currentMeta.region) ?? null,
        ItemTypeKey: resolvedItemTypeKey,
        ItemType: itemCtx.ItemType ?? baseRow.ItemType ?? "",
        ItemTypeSort: resolvedItemTypeSort,
        itemTypeSort: resolvedItemTypeSort,
        FitmentLocationKey: resolvedLocationKey,
        FitmentLocation:
          locationCtx.FitmentLocation ??
          locationCtx.location ??
          locationCtx.name ??
          baseRow.FitmentLocation ??
          "",
  LocationSort: locationSort,
  LocSortOrder: locationSort,
        FitmentKey: null,
        OEMKey: null,
        OEMNumber: "",
        SKUGroup: "",
        Quantity: null,
        OEMNote: "",
        AdditionalFitmentInfo: "",
        FicheURL: "",
        DateCreated: null,
        DateLastUpdated: null,
        IsGhost: true,
        isGhost: true,
        IsGhostOneOff: true,
        isGhostOneOff: true,
        IsOneOff: true,
        isOneOff: true,
        OneOffGroupKey: locationCtx.OneOffGroupKey ?? baseRow.OneOffGroupKey ?? null,
        ModelSortOrder: modelCtx.ModelSortOrder ?? baseRow.ModelSortOrder ?? 0,
        _section: "current",
        IsCurrent: true,
        SourceTag: "oneoff"
      };

      const identity = rowIdentityKey(ghostRow);

      setRowsState((prev) => {
        if (prev.some((row) => rowIdentityKey(row) === identity)) {
          const next = prev.map((row) =>
            rowIdentityKey(row) === identity ? { ...row, ...ghostRow } : row
          );
          rowsStateRef.current = next;
          return next;
        }

        const next = [...prev, ghostRow];
        next.sort(compareRows);
        rowsStateRef.current = next;
        return next;
      });
    }, [currentMeta, displayedModelKey, modelKey]);

  const handleOneOffPromoted = useCallback((payload) => {
    if (!payload) return;
    setRowsState((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (
          row.ItemTypeKey === payload.ItemTypeKey &&
          row.FitmentLocationKey === payload.FitmentLocationKey
        ) {
          changed = true;
          return {
            ...row,
            OneOffGroupKey: payload.OneOffGroupKey ?? row.OneOffGroupKey ?? null,
            IsOneOff: true,
            isOneOff: true
          };
        }
        return row;
      });
      if (!changed) return prev;
      rowsStateRef.current = next;
      return next;
    });
  }, []);
  const selectionSummary = useMemo(() => {
    if (!selectedManageRow) return "No current line selected";
    const pieces = [
      selectedManageRow.ItemType || "",
      selectedManageRow.FitmentLocation || "",
      selectedManageRow.Year !== null && selectedManageRow.Year !== undefined
        ? selectedManageRow.Year
        : ""
    ].filter(Boolean);
    return pieces.length > 0 ? pieces.join(" • ") : "Current line selected";
  }, [selectedManageRow]);

  const addOneOffButtonTitle = useMemo(() => {
    if (!canWrite) return "You do not have permission to add one-off rows";
    if (!selectedManageRow) return "Select a current-year line to add a one-off row";
    return "Add a one-off fitment location for this item type";
  }, [canWrite, selectedManageRow]);

  const manageOEMButtonTitle = useMemo(() => {
    if (!canWrite) return "You do not have permission to manage OEM values";
    if (!selectedManageRow) return "Select a current-year line to manage";
    return "Manage OEM for selected line";
  }, [canWrite, selectedManageRow]);

  const manageSKUButtonTitle = useMemo(() => {
    if (!canWrite) return "You do not have permission to manage SKU groups";
    if (!selectedManageRow) return "Select a current-year line to manage";
    return "Manage SKU Group for selected line";
  }, [canWrite, selectedManageRow]);


  const maybeFormatOEMValue = useCallback(
    async (value, row) => {
  const trimmed = valueToString(value).trim();
      if (!formatOEMEnabled || !trimmed || typeof resolveOEMTypeFast !== "function") {
        // Still try to resolve and cache OEMType even if not formatting
        if (trimmed && typeof resolveOEMTypeFast === "function") {
          try {
            const oemType = await resolveOEMTypeFast(trimmed);
            if (oemType) {
              oemTypeCacheRef.current.set(trimmed, oemType);
            }
          } catch (err) {
            // Ignore cache errors
          }
        }
        return value;
      }

      try {
        const oemType = await resolveOEMTypeFast(trimmed);
        // Cache the OEMType for hide completed logic
        if (oemType) {
          oemTypeCacheRef.current.set(trimmed, oemType);
        }
        if (oemType && oemType !== "Misc" && oemType !== "NonOEM") {
          const manufacturer =
            row.Make ?? (currentMeta && currentMeta.make) ?? "Honda";
          const formatRes = await axios.post("/api/format-oem", {
            oem: trimmed,
            manufacturer
          });
          const formatted = formatRes?.data?.formatted;
          if (formatted && String(formatted).trim()) {
            return formatted;
          }
        }
      } catch (err) {
        console.error("OEM auto-format failed", err);
      }

      return value;
    },
    [formatOEMEnabled, resolveOEMTypeFast, currentMeta]
  );

  // Generic edit commit for current rows
  async function commitEdit(rowIdentity, field, valueOverride, options = {}) {
    const currentRows = rowsStateRef.current;
    const targetIndex = findRowIndexByIdentity(currentRows, rowIdentity);
    const row = targetIndex >= 0 ? currentRows[targetIndex] : null;

    if (!row || !row.IsCurrent) {
      setEditing({ rowIdentity: "", field: "", value: "" });
      return rowIdentity;
    }

    const originalFitmentKey = row.FitmentKey ?? null;
    const effectiveFitmentKey = options.forceFitmentKey ?? originalFitmentKey;
    const isCreateOperation = !effectiveFitmentKey;
    const createKey = createKeyForRow(row);
    const originalValue = row[field];
    const editValue =
      valueOverride !== null && valueOverride !== undefined ? valueOverride : editing.value;
    let nextValue = editValue;
    let addedCreateKey = false;
    let finalIdentity = rowIdentity;

    try {
      if (field === "OEMNumber") {
        nextValue = await maybeFormatOEMValue(editValue, row);
      }

      setRowsState((prev) => {
        const next = prev.map((r) =>
          rowIdentityKey(r) === rowIdentity ? { ...r, [field]: nextValue } : r
        );
        rowsStateRef.current = next;
        return next;
      });

      if (isCreateOperation && createInFlightRef.current.has(createKey) && !options.allowDuringCreate) {
        const pending = pendingCreateEditsRef.current.get(createKey) || {
          identity: rowIdentity,
          fields: {}
        };
        pending.identity = rowIdentity;
        pending.fields[field] = nextValue;
        pendingCreateEditsRef.current.set(createKey, pending);
        return rowIdentity;
      }

      if (isCreateOperation) {
        createInFlightRef.current.add(createKey);
        addedCreateKey = true;
      }

      const payloadValue =
        nextValue === null ||
        nextValue === undefined ||
        (typeof nextValue === "string" && nextValue.trim() === "")
          ? null
          : nextValue;

      let apiRes;
      if (isCreateOperation) {
        apiRes = await axios.post("/api/fitments", {
          modelKey: row.ModelKey,
          fitmentLocationKey: row.FitmentLocationKey,
          itemTypeKey: row.ItemTypeKey,
          field,
          value: payloadValue
        });
      } else {
        apiRes = await axios.patch(`/api/fitments/${effectiveFitmentKey}`, {
          field,
          value: payloadValue
        });
      }

      const updated = apiRes?.data?.updated ?? null;
      const responseFitmentKey = updated?.FitmentKey ?? effectiveFitmentKey;
      const resolvedIdentity = responseFitmentKey ? `fit:${responseFitmentKey}` : rowIdentity;
      finalIdentity = resolvedIdentity;

      if (updated) {
        setRowsState((prev) => {
          const next = prev.map((r) => {
            if (rowIdentityKey(r) !== rowIdentity) return r;
            const merged = { ...r, ...updated };
            if (field === "OEMNumber") {
              const nextOem = typeof merged.OEMNumber === "string" ? merged.OEMNumber.trim() : "";
              const responseIsKit = updated?.IsKit;
              const normalizedIsKit = responseIsKit != null
                ? responseIsKit === true || responseIsKit === 1 || responseIsKit === "1"
                : nextOem.toUpperCase().startsWith(KIT_ID_PREFIX);
              merged.IsKit = normalizedIsKit;
              const responseKitId = updated?.KitId ?? updated?.KitID;
              const resolvedKitId = responseKitId || (normalizedIsKit ? nextOem : null);
              merged.KitId = resolvedKitId || null;
              merged.KitID = resolvedKitId || null;
            }
            const rfList = merged.RecentFields
              ? String(merged.RecentFields)
                  .split(",")
                  .map((x) => String(x).trim())
              : [];
            const rfSet = new Set(rfList);
            rfSet.add(field);
            merged.RecentFields = Array.from(rfSet).join(",");
            if (isCreateOperation && !merged.DateCreated) {
              merged.DateCreated = new Date().toISOString();
            }
            if (responseFitmentKey && !merged.FitmentKey) {
              merged.FitmentKey = responseFitmentKey;
            }
            return merged;
          });
          rowsStateRef.current = next;
          return next;
        });
        if (resolvedIdentity !== rowIdentity) {
          setSelectedCell((prev) => {
            if (prev.rowIdentity !== rowIdentity) return prev;
            return { ...prev, rowIdentity: resolvedIdentity };
          });
        }
      }

      if (isCreateOperation) {
        const pending = pendingCreateEditsRef.current.get(createKey);
        if (pending && responseFitmentKey) {
          pending.identity = resolvedIdentity;
          pendingCreateEditsRef.current.delete(createKey);
          for (const [pendingField, pendingValue] of Object.entries(pending.fields)) {
            if (pendingField === field && pendingValue === nextValue) continue;
            await commitEdit(resolvedIdentity, pendingField, pendingValue, {
              allowDuringCreate: true,
              forceFitmentKey: responseFitmentKey
            });
          }
        }
      }
    } catch (err) {
      console.error((originalFitmentKey ? "Update" : "Create") + " failed", err);
      window.alert(err?.response?.data?.error || "Save failed—see console.");
      setRowsState((prev) => {
        const next = prev.map((r) =>
          rowIdentityKey(r) === rowIdentity ? { ...r, [field]: originalValue } : r
        );
        rowsStateRef.current = next;
        return next;
      });
      if (isCreateOperation) {
        pendingCreateEditsRef.current.delete(createKey);
      }
    } finally {
      if (addedCreateKey) {
        createInFlightRef.current.delete(createKey);
      }
      if (!options.deferResetEditing) {
        setEditing({ rowIdentity: "", field: "", value: "" });
      }
    }

    return finalIdentity;
  }

  function handleChange(rowIdentity, field, value) {
    setEditing({ rowIdentity, field, value });
  }

  function handleBlur(rowIdentity, field) {
    if (editing.rowIdentity === rowIdentity && editing.field === field) {
      const v = editing.value;
      setEditing({ rowIdentity: "", field: "", value: "" });

      if (field === "FicheURL") {
        handleFicheUrlBlur(rowIdentity, v);
      } else {
        commitEdit(rowIdentity, field, v);
      }
    }
  }

  function handleKeyDown(e, rowIdentity, field) {
    const key = e.key;
    const isTab = key === "Tab";
    const isEnter = key === "Enter";
    if (isTab && e.shiftKey) {
      return;
    }
    const isForwardNav = isEnter || (isTab && !e.shiftKey);

    if (ARROW_KEYS.has(key)) {
      const fieldIndex = EDITABLE_FIELD_ORDER.indexOf(field);
      if (fieldIndex !== -1) {
        let targetField = field;
        let targetIdentity = rowIdentity;
        let handled = false;

        if (key === "ArrowLeft") {
          if (fieldIndex > 0) {
            targetField = EDITABLE_FIELD_ORDER[fieldIndex - 1];
            handled = true;
          } else {
            const prevRow = findAdjacentEditableRow(rowIdentity, -1);
            if (prevRow) {
              targetIdentity = prevRow;
              targetField = EDITABLE_FIELD_ORDER[EDITABLE_FIELD_ORDER.length - 1];
            }
            handled = true;
          }
        } else if (key === "ArrowRight") {
          if (fieldIndex < EDITABLE_FIELD_ORDER.length - 1) {
            targetField = EDITABLE_FIELD_ORDER[fieldIndex + 1];
            handled = true;
          } else {
            const nextRow = findAdjacentEditableRow(rowIdentity, 1);
            if (nextRow) {
              targetIdentity = nextRow;
              targetField = EDITABLE_FIELD_ORDER[0];
            }
            handled = true;
          }
        } else {
          const direction = key === "ArrowDown" ? 1 : -1;
          const adjacent = findAdjacentEditableRow(rowIdentity, direction);
          targetIdentity = adjacent || rowIdentity;
          handled = true;
        }

        if (handled) {
          e.preventDefault();
          scheduleFocusEditableCell(targetIdentity, targetField);
          return;
        }
      }
    }

    if (field === "OEMNumber" && isForwardNav) {
      e.preventDefault();
      const value = e.target.value;
      setEditing({ rowIdentity: "", field: "", value: "" });
      const trimmedValue = normalizeCopyField(value);
      const currentRows = rowsStateRef.current || [];
      const currentIndex = findRowIndexByIdentity(currentRows, rowIdentity);
      const currentRow = currentIndex >= 0 ? currentRows[currentIndex] : null;
      const hadOEM = currentRow
        ? normalizeCopyField(currentRow.OEMNumber).length > 0
        : false;

      if (trimmedValue.length === 0) {
        if (!hadOEM) {
          focusNextAvailableOEM(rowIdentity);
          return;
        }

        const result = commitEdit(rowIdentity, field, "");
        const onComplete = () => focusNextAvailableOEM(rowIdentity);
        if (result && typeof result.then === "function") {
          result.then(onComplete);
        } else {
          onComplete();
        }
        return;
      }

      const focusQuantity = (resolvedIdentity) => {
        const target = resolvedIdentity || rowIdentity;
        scheduleFocusEditableCell(target, "Quantity");
      };
      const result = commitEdit(rowIdentity, field, value);
      if (result && typeof result.then === "function") {
        result.then(focusQuantity);
      } else {
        focusQuantity(rowIdentity);
      }
      return;
    }

    if (field === "Quantity" && isForwardNav) {
      e.preventDefault();
      const value = e.target.value;
      setEditing({ rowIdentity: "", field: "", value: "" });
      const trimmedQuantity = normalizeCopyField(value);
      const currentRows = rowsStateRef.current || [];
      const currentIndex = findRowIndexByIdentity(currentRows, rowIdentity);
      const currentRow = currentIndex >= 0 ? currentRows[currentIndex] : null;
      const hadQuantity = currentRow
        ? currentRow.Quantity !== null && currentRow.Quantity !== undefined
        : false;

      if (trimmedQuantity.length === 0 && !hadQuantity) {
        focusNextAvailableOEM(rowIdentity);
        return;
      }

      const focusNext = (resolvedIdentity) => {
        const reference = resolvedIdentity || rowIdentity;
        focusNextAvailableOEM(reference, "Quantity");
      };
      const result = commitEdit(rowIdentity, field, value);
      if (result && typeof result.then === "function") {
        result.then(focusNext);
      } else {
        focusNext(rowIdentity);
      }
      return;
    }

    if (isEnter) {
      e.preventDefault();
      const value = e.target.value;
      setEditing({ rowIdentity: "", field: "", value: "" });
      if (field === "FicheURL") handleFicheUrlBlur(rowIdentity, value);
      else commitEdit(rowIdentity, field, value);
    }
  }

  async function handleFicheUrlBlur(rowIdentity, value) {
    const currentRows = rowsStateRef.current;
    const rowIndex = findRowIndexByIdentity(currentRows, rowIdentity);
    const row = rowIndex >= 0 ? currentRows[rowIndex] : null;
    if (!row || !row.IsCurrent) return;

    let fk = row.FitmentKey;
    const createKey = createKeyForRow(row);
    let addedCreateKey = false;

    try {
      if (!fk) {
        if (createInFlightRef.current.has(createKey)) {
          setTimeout(() => handleFicheUrlBlur(rowIdentity, value), 200);
          return;
        }
        createInFlightRef.current.add(createKey);
        addedCreateKey = true;
        const createRes = await axios.post("/api/fitments", {
          modelKey: row.ModelKey,
          fitmentLocationKey: row.FitmentLocationKey,
          itemTypeKey: row.ItemTypeKey,
          field: "OEMNumber",
          value: row.OEMNumber || null
        });
        fk = createRes?.data?.updated?.FitmentKey;
        if (!fk) throw new Error("Failed to create Fitment for FicheURL");
        setRowsState((prev) => {
          const next = prev.map((r) =>
            rowIdentityKey(r) === rowIdentity ? { ...r, FitmentKey: fk } : r
          );
          rowsStateRef.current = next;
          return next;
        });
      }

      if (value && String(value).trim()) {
        await axios.post(`/api/fitments/${fk}/fiches`, { url: value });
      }

      setRowsState((prev) => {
        const next = prev.map((r) =>
          rowIdentityKey(r) === rowIdentity
            ? { ...r, FicheURL: r.FicheURL ? `${r.FicheURL},${value}` : value }
            : r
        );
        rowsStateRef.current = next;
        return next;
      });
    } catch (err) {
      console.error("FicheURL update failed:", err);
      window.alert("Failed to process fiche URL — see console.");
    } finally {
      if (addedCreateKey) {
        createInFlightRef.current.delete(createKey);
      }
    }
  }

  // Derive value by key (helps when meta holds identity)
  const valueByKey = (row, key) => {
    switch (key) {
      case "Make":
        return row.Make ?? (currentMeta && currentMeta.make) ?? "";
      case "ModelName":
        return row.ModelName ?? (currentMeta && currentMeta.modelName) ?? "";
      case "Region":
        return row.Region ?? (currentMeta && currentMeta.region) ?? "";
      case "Year":
        return row.Year ?? (currentMeta && currentMeta.year) ?? "";
      default:
        return row[key];
    }
  };

  const openHistory = useCallback(async (fitmentKey) => {
    if (!fitmentKey) return;
    setHistoryFitmentKey(fitmentKey);
    setHistoryVisible(true);
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const res = await axios.get(`/api/fitments/history?fitmentKey=${fitmentKey}`);
      const payload = res?.data;
      const records = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.records)
        ? payload.records
        : Array.isArray(payload?.history)
        ? payload.history
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.recordset)
        ? payload.recordset
        : [];
      setHistoryRecords(records);
    } catch (err) {
      console.error("Failed to fetch history", err);
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        "Failed to load history. Please try again.";
      setHistoryError(msg);
      setHistoryRecords([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const closeHistory = useCallback(() => {
    setHistoryVisible(false);
  }, []);

  const handleHistoryUndo = useCallback(
    async (entry) => {
      if (!canWrite) {
        window.alert("You do not have permission to revert history entries.");
        return;
      }
      if (!entry || !entry.FitmentKey) return;
      const ok = window.confirm(
        `Revert "${entry.FieldName}" on ${entry.Make} ${entry.ModelName} ${entry.Year} (${entry.ItemType})?\n` +
          `from "${entry.NewValue}" back to "${entry.OldValue}"?`
      );
      if (!ok) return;
      setHistoryLoading(true);
      setHistoryError("");
      try {
        await axios.patch(`/api/fitments/${entry.FitmentKey}`, {
          field: entry.FieldName,
          value: entry.OldValue
        });
        refreshData();
        await openHistory(entry.FitmentKey);
      } catch (err) {
        console.error("Failed to undo history entry", err);
        window.alert("Failed to undo change—see console.");
        setHistoryLoading(false);
      }
    },
    [canWrite, openHistory, refreshData]
  );

  const handleHistoryRedo = useCallback(
    async (entry) => {
      if (!canWrite) {
        window.alert("You do not have permission to re-apply history entries.");
        return;
      }
      if (!entry || !entry.FitmentKey) return;
      const ok = window.confirm(
        `Re-apply "${entry.FieldName}" on ${entry.Make} ${entry.ModelName} ${entry.Year} (${entry.ItemType})?\n` +
          `to "${entry.NewValue}"?`
      );
      if (!ok) return;
      setHistoryLoading(true);
      setHistoryError("");
      try {
        await axios.patch(`/api/fitments/${entry.FitmentKey}`, {
          field: entry.FieldName,
          value: entry.NewValue
        });
        refreshData();
        await openHistory(entry.FitmentKey);
      } catch (err) {
        console.error("Failed to redo history entry", err);
        window.alert("Failed to redo change—see console.");
        setHistoryLoading(false);
      }
    },
    [canWrite, openHistory, refreshData]
  );

  // Filter rows (client-side “contains”, case-insensitive)
  const filteredRows = useMemo(() => {
    const activeKeys = Object.keys(filters).filter((k) => String(filters[k]).trim());
    if (activeKeys.length === 0) return rowsAfterHide;

    return rowsAfterHide.filter((row) => {
      return activeKeys.every((k) => {
        const hay = String(valueByKey(row, k) ?? "").toLowerCase();
        const tokens = String(filters[k]).toLowerCase().trim().split(/\s+/).filter(Boolean);
        // Wildcard multi-word match: every word must appear somewhere in the
        // value, regardless of order (e.g. "front brake" matches "Brake - Front").
        return tokens.every((tok) => hay.includes(tok));
      });
    });
  }, [rowsAfterHide, filters]);

  // Keep filteredRowsRef in sync for navigation functions
  useEffect(() => {
    filteredRowsRef.current = filteredRows;
  }, [filteredRows]);

  // ─── Multi-row selection for "Create Kit from Selected" ──────────────────
  const selectableCurrentIds = useMemo(
    () => filteredRows.filter((row) => row.IsCurrent).map((row) => rowIdentityKey(row)),
    [filteredRows]
  );

  const allCurrentSelected =
    selectableCurrentIds.length > 0 &&
    selectableCurrentIds.every((id) => selectedKitRowIds.has(id));

  const toggleKitRowSelection = useCallback((identity) => {
    if (!identity) return;
    setSelectedKitRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(identity)) next.delete(identity);
      else next.add(identity);
      return next;
    });
  }, []);

  const toggleSelectAllCurrent = useCallback(() => {
    setSelectedKitRowIds((prev) => {
      const next = new Set(prev);
      const allSelected =
        selectableCurrentIds.length > 0 &&
        selectableCurrentIds.every((id) => next.has(id));
      if (allSelected) {
        selectableCurrentIds.forEach((id) => next.delete(id));
      } else {
        selectableCurrentIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [selectableCurrentIds]);

  const buildKitComponentsFromSelection = useCallback(() => {
    const rows = rowsStateRef.current || [];
    const comps = [];
    selectedKitRowIds.forEach((identity) => {
      const idx = findRowIndexByIdentity(rows, identity);
      const row = idx >= 0 ? rows[idx] : null;
      if (!row) return;
      const oem = normalizeCopyField(row.OEMNumber);
      if (!oem) return; // skip rows without an OEM Number
      const qty = Number(row.Quantity);
      const seed = {
        ComponentOEMNumber: oem,
        Quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
        OEMNote: normalizeCopyField(row.OEMNote),
        ItemTypeKey: row.ItemTypeKey ?? null,
        FitmentLocationKey: row.FitmentLocationKey ?? null,
        OEMKey: row.OEMKey ?? null,
        SKU: normalizeCopyField(row.SKU),
        // Display-only helpers (ignored by the kit save payload)
        ItemType: normalizeCopyField(row.ItemType),
        FitmentLocation: normalizeCopyField(row.FitmentLocation)
      };
      const skuGroup = normalizeCopyField(row.SKUGroup);
      if (skuGroup) seed.SKUGroup = skuGroup;
      comps.push(seed);
    });
    return comps;
  }, [selectedKitRowIds]);

  const handleOpenKitFromSelection = useCallback(() => {
    if (!canManageKits) {
      window.alert("You do not have permission to manage kits.");
      return;
    }
    const comps = buildKitComponentsFromSelection();
    if (comps.length === 0) {
      window.alert(
        "Select at least one current-year line that has an OEM Number to build a kit from."
      );
      return;
    }
    const defaultTarget = selectedManageRow
      ? rowIdentityKey(selectedManageRow)
      : Array.from(selectedKitRowIds)[0] || "";
    setKitBuilder({ components: comps, targetIdentity: defaultTarget });
  }, [canManageKits, buildKitComponentsFromSelection, selectedManageRow, selectedKitRowIds]);

  const kitBuilderTargetOptions = useMemo(() => {
    return (rowsState || [])
      .filter((row) => row.IsCurrent)
      .map((row) => ({
        identity: rowIdentityKey(row),
        label:
          `${normalizeCopyField(row.ItemType) || "—"} / ${normalizeCopyField(row.FitmentLocation) || "—"}` +
          (normalizeCopyField(row.OEMNumber) ? ` (has ${normalizeCopyField(row.OEMNumber)})` : "")
      }));
  }, [rowsState]);

  const confirmKitBuilder = useCallback(() => {
    if (!kitBuilder) return;
    const targetId = kitBuilder.targetIdentity;
    if (!targetId) {
      window.alert("Choose an Item Type / Fitment Location to insert the Kit number into.");
      return;
    }
    const rows = rowsStateRef.current || [];
    const idx = findRowIndexByIdentity(rows, targetId);
    const targetRow = idx >= 0 ? rows[idx] : null;
    if (!targetRow) {
      window.alert("Unable to resolve the selected target line. Please try again.");
      return;
    }

    const fitmentContext = {
      FitmentKey: targetRow.FitmentKey ?? null,
      ItemTypeKey: targetRow.ItemTypeKey ?? null,
      ItemType: targetRow.ItemType ?? "",
      FitmentLocationKey: targetRow.FitmentLocationKey ?? null,
      FitmentLocation: targetRow.FitmentLocation ?? "",
      // Deliberately blank so the kit editor seeds from initialComponents, not the target OEM.
      OEMNumber: "",
      SKUGroup: targetRow.SKUGroup ?? "",
      ModelKey: targetRow.ModelKey ?? displayedModelKey ?? modelKey ?? null,
      Make: targetRow.Make ?? currentMeta?.make ?? "",
      ModelName: targetRow.ModelName ?? currentMeta?.modelName ?? "",
      Year: targetRow.Year ?? currentMeta?.year ?? null,
      Region: targetRow.Region ?? currentMeta?.region ?? null,
      Quantity: targetRow.Quantity ?? null,
      OEMNote: targetRow.OEMNote ?? "",
      rowIdentity: rowIdentityKey(targetRow)
    };

    setKitModalState({
      open: true,
      kitId: null,
      fitment: fitmentContext,
      initialComponents: kitBuilder.components
    });
    setKitBuilder(null);
  }, [kitBuilder, displayedModelKey, modelKey, currentMeta]);

  const copyModelFromSection = useCallback(
    async (section) => {
      if (!canWrite) {
        window.alert("You do not have permission to copy model data.");
        return;
      }

      const baseBlock = section === "previous" ? data?.previous : data?.next;
      const navData = await collectNavOptions(section).catch((err) => {
        console.error("Failed to gather copy candidates", err);
        return null;
      });

      const directionLabel = section === "previous" ? "previous" : "next";
      const noteMarker = directionLabel.toUpperCase();
      const navOptions = Array.isArray(navData?.options) ? navData.options : [];
      const baseLabel = navData?.baseLabel || formatLabel(baseBlock?.meta, directionLabel);

      let selectedOption = navOptions[0] ?? navData?.fallbackCandidate ?? null;
      let sourceModelKey =
        selectedOption?.ModelKey ?? navData?.targetModelKey ?? baseBlock?.meta?.ModelKey ?? baseBlock?.meta?.modelKey ?? null;

      if (navOptions.length > 1) {
        try {
          const { candidate } = await requestNavChoice({
            mode: "copy",
            direction: section,
            baseLabel,
            options: navOptions
          });
          if (candidate) {
            selectedOption = candidate;
            if (candidate.ModelKey) {
              sourceModelKey = candidate.ModelKey;
            }
          }
        } catch (err) {
          return;
        }
      }

      const sourceBlock = await resolveSourceBlock(sourceModelKey, baseBlock);
      if (!sourceBlock || !Array.isArray(sourceBlock.rows) || sourceBlock.rows.length === 0) {
        window.alert(`No ${directionLabel} year data is available to copy.`);
        return;
      }

      const sourceLabel = formatLabel(sourceBlock.meta, baseLabel);
      const proceed = window.confirm(
        `Copy ${directionLabel} year data${sourceLabel ? ` from ${sourceLabel}` : ""} into the current model?\n` +
          "Blank fields will be filled automatically. You will choose whether to overwrite differing values."
      );
      if (!proceed) return;

      setCopyingSection(section);
      try {
        const sourceBuckets = new Map();
        sourceBlock.rows.forEach((row) => {
          const key = comboKey(row);
          if (!key) return;
          const bucket = sourceBuckets.get(key) || [];
          bucket.push(row);
          sourceBuckets.set(key, bucket);
        });

        const currentRowsList = (rowsStateRef.current || []).filter((row) => {
          const section = row._section || (row.IsCurrent ? "current" : "sibling");
          return section === "current";
        });

        const targetBuckets = new Map();
        currentRowsList.forEach((row) => {
          const key = comboKey(row);
          if (!key) return;
          const list = targetBuckets.get(key) || [];
          list.push(row);
          targetBuckets.set(key, list);
        });

        const selectTargetOrder = (rows) => {
          return rows
            .slice()
            .sort((a, b) => {
              const ghostA = isGhostRow(a) ? 1 : 0;
              const ghostB = isGhostRow(b) ? 1 : 0;
              if (ghostA !== ghostB) return ghostA - ghostB; // prefer non-ghost rows

              const dataA = rowHasMeaningfulData(a) ? 0 : 1;
              const dataB = rowHasMeaningfulData(b) ? 0 : 1;
              if (dataA !== dataB) return dataA - dataB; // prefer rows already holding data

              const fitmentA = a.FitmentKey ? 0 : 1;
              const fitmentB = b.FitmentKey ? 0 : 1;
              if (fitmentA !== fitmentB) return fitmentA - fitmentB; // prefer persisted rows

              return (a.__tempId ?? a._tempId ?? a.FitmentKey ?? "").toString().localeCompare(
                (b.__tempId ?? b._tempId ?? b.FitmentKey ?? "").toString()
              );
            });
        };

        const assignments = [];
        targetBuckets.forEach((targets, key) => {
          const sourceList = sourceBuckets.get(key);
          if (!sourceList || sourceList.length === 0) return;
          const orderedTargets = selectTargetOrder(targets);
          for (let idx = 0; idx < orderedTargets.length && sourceList.length > 0; idx++) {
            const targetRow = orderedTargets[idx];
            const sourceRow = sourceList.shift();
            assignments.push({ row: targetRow, sourceRow });
          }
        });

        const blankUpdatesByRow = new Map();
        const conflictAssignments = [];

        assignments.forEach(({ row, sourceRow }) => {
          const { updates, hasData } = extractCopyUpdates(sourceRow);
          if (!hasData) return;

          const identity = rowIdentityKey(row);
          const blanks = [];
          const conflicts = [];

          updates.forEach(({ field, value }) => {
            const currentVal = row[field];
            const hasCurrent = field === "Quantity"
              ? currentVal !== null && currentVal !== undefined
              : normalizeCopyField(currentVal).length > 0;

            if (!hasCurrent) {
              blanks.push({ field, value });
              return;
            }

            if (!valuesEqual(field, currentVal, value)) {
              conflicts.push({ field, value, currentValue: currentVal });
            }
          });

          if (blanks.length > 0) {
            const existing = blankUpdatesByRow.get(identity) || [];
            blankUpdatesByRow.set(identity, existing.concat(blanks));
          }

          if (conflicts.length > 0) {
            conflictAssignments.push({ row, rowIdentity: identity, sourceRow, conflicts, updates });
          }
        });

        const extraSources = [];
        let skippedDuplicateSources = 0;
        sourceBuckets.forEach((bucket) => {
          bucket.forEach((sourceRow) => {
            const { updates, hasData, hasOEM } = extractCopyUpdates(sourceRow);
            if (!hasData) return;

            const matchingTarget = currentRowsList.find((candidate) => {
              if (!candidate) return false;

              const sameItemTypeKey = (candidate.ItemTypeKey ?? null) === (sourceRow.ItemTypeKey ?? null);
              const sameItemTypeLabel = normalizeLower(candidate.ItemType) === normalizeLower(sourceRow.ItemType);
              if (!sameItemTypeKey && !sameItemTypeLabel) return false;

              const sameLocationKey =
                (candidate.FitmentLocationKey ?? null) === (sourceRow.FitmentLocationKey ?? null);
              const sameLocationLabel =
                normalizeLower(candidate.FitmentLocation) === normalizeLower(sourceRow.FitmentLocation);
              if (!sameLocationKey && !sameLocationLabel) return false;

              if (
                normalizeCopyField(candidate.AdditionalFitmentInfo) !==
                normalizeCopyField(sourceRow.AdditionalFitmentInfo)
              ) {
                return false;
              }

              return updates.every(({ field, value }) => valuesEqual(field, candidate[field], value));
            });

            if (matchingTarget) {
              skippedDuplicateSources += 1;
              return;
            }

            extraSources.push({ sourceRow, updates, hasOEM });
          });
        });

        let skippedForMissingFitment = 0;
        let skippedExtraWithoutOEM = 0;

        for (const [identity, updates] of blankUpdatesByRow.entries()) {
          try {
            await runSequentialCopy(identity, noteMarker, updates);
          } catch (err) {
            const message = err?.response?.data?.error || err?.message || "Bulk copy failed.";
            if (err?.message === "Target fitment has no record yet—copy an OEM Number first.") {
              skippedForMissingFitment += 1;
            } else {
              console.error("Bulk copy failed for row", identity, err);
              window.alert(`Failed to copy data for ${identity}. See console for details.`);
            }
          }
        }

        const conflictItems = [];
        if (conflictAssignments.length > 0) {
          const ordered = conflictAssignments.sort((a, b) => {
            const itemCompare = String(a.row.ItemType || "").localeCompare(String(b.row.ItemType || ""));
            if (itemCompare !== 0) return itemCompare;
            return String(a.row.FitmentLocation || "").localeCompare(String(b.row.FitmentLocation || ""));
          });

          for (let idx = 0; idx < ordered.length; idx++) {
            const assignment = ordered[idx];
            const identity = assignment.rowIdentity;
            const latestRow =
              rowsStateRef.current.find((row) => rowIdentityKey(row) === identity) || assignment.row;

            const differences = assignment.conflicts.map((conflict) => ({
              field: conflict.field,
              currentValue: displayValue(conflict.field, conflict.currentValue) || "—",
              incomingValue: displayValue(conflict.field, conflict.value) || "—"
            }));

            conflictItems.push({
              id: `conflict-${identity}-${idx}`,
              assignment,
              identity,
              itemType: latestRow.ItemType || "",
              fitmentLocation: latestRow.FitmentLocation || "",
              additionalInfo: latestRow.AdditionalFitmentInfo || "",
              differences,
              defaultDecision: "override"
            });
          }
        }

        const extraItems = [];
        if (extraSources.length > 0) {
          extraSources.sort((a, b) => {
            const itemCompare = String(a.sourceRow.ItemType || "").localeCompare(
              String(b.sourceRow.ItemType || "")
            );
            if (itemCompare !== 0) return itemCompare;
            const locationCompare = String(a.sourceRow.FitmentLocation || "").localeCompare(
              String(b.sourceRow.FitmentLocation || "")
            );
            if (locationCompare !== 0) return locationCompare;
            return String(a.sourceRow.AdditionalFitmentInfo || "").localeCompare(
              String(b.sourceRow.AdditionalFitmentInfo || "")
            );
          });

          extraSources.forEach(({ sourceRow, updates, hasOEM }, extraIdx) => {
            if (!hasOEM) {
              skippedExtraWithoutOEM += 1;
              return;
            }

            extraItems.push({
              id: `extra-${comboKey(sourceRow)}-${extraIdx}`,
              sourceRow,
              updates,
              itemType: sourceRow.ItemType || "",
              fitmentLocation: sourceRow.FitmentLocation || "",
              additionalInfo: sourceRow.AdditionalFitmentInfo || "",
              defaultDecision: "add"
            });
          });
        }

        let decisionMap = {};
        if (conflictItems.length > 0 || extraItems.length > 0) {
          try {
            const reviewPayload = {
              directionLabel,
              noteMarker,
              conflicts: conflictItems,
              extras: extraItems,
              duplicatesSkipped: skippedDuplicateSources
            };
            decisionMap = await requestBulkCopyDecisions(reviewPayload);
          } catch (err) {
            console.info("Bulk copy cancelled by user", err);
            return;
          }
        }

        for (let i = 0; i < conflictItems.length; i++) {
          const item = conflictItems[i];
          const decision = decisionMap[item.id] || "ignore";
          if (decision === "ignore") continue;

          if (decision === "override") {
            const overridePayload = item.assignment.conflicts.map(({ field, value }) => ({ field, value }));
            try {
              await runSequentialCopy(item.identity, noteMarker, overridePayload);
            } catch (err) {
              console.error("Failed to override during bulk copy", item.assignment, err);
              const message = err?.response?.data?.error || err?.message || "Failed to overwrite value.";
              window.alert(`${message} See console for details.`);
            }
            continue;
          }

          if (decision === "add") {
            if (!item.assignment.updates.some((u) => u.field === "OEMNumber")) {
              skippedForMissingFitment += 1;
              window.alert(
                `Cannot add a new row for ${item.itemType || ""} / ${item.fitmentLocation || ""} because the ${directionLabel} data has no OEM Number.`
              );
              continue;
            }
            const duplicate = createAdditionalCurrentRow(item.assignment.sourceRow, noteMarker);
            if (!duplicate) {
              window.alert("Unable to create an additional row for this copy.");
              continue;
            }
            try {
              await runSequentialCopy(rowIdentityKey(duplicate), noteMarker, item.assignment.updates);
            } catch (err) {
              console.error("Failed to add duplicate row during bulk copy", item.assignment, err);
              const message = err?.response?.data?.error || err?.message || "Failed to add new row.";
              window.alert(`${message} See console for details.`);
            }
          }
        }

        for (let i = 0; i < extraItems.length; i++) {
          const item = extraItems[i];
          const decision = decisionMap[item.id] || "add";
          if (decision !== "add") continue;

          const seeded = {
            ...item.sourceRow,
            _section: "current",
            IsCurrent: true
          };
          const newRow = createAdditionalCurrentRow(seeded, noteMarker);
          if (!newRow) {
            window.alert("Unable to create a new row for this copy.");
            continue;
          }

          try {
            await runSequentialCopy(rowIdentityKey(newRow), noteMarker, item.updates);
          } catch (err) {
            console.error("Failed to add extra source row during bulk copy", item.sourceRow, err);
            const message = err?.response?.data?.error || err?.message || "Failed to add new row.";
            window.alert(`${message} See console for details.`);
          }
        }

        if (skippedForMissingFitment > 0) {
          window.alert(
            `${skippedForMissingFitment} row(s) could not be updated because an OEM Number was required to create a fitment record.`
          );
        }

        if (skippedExtraWithoutOEM > 0) {
          window.alert(
            `${skippedExtraWithoutOEM} additional ${directionLabel} row(s) were skipped because they did not contain an OEM Number.`
          );
        }

        await refreshData();
      } finally {
        setCopyingSection("");
      }
    },
    [
      canWrite,
      collectNavOptions,
      createAdditionalCurrentRow,
      data,
      refreshData,
      requestBulkCopyDecisions,
      requestNavChoice,
      resolveSourceBlock,
      runSequentialCopy
    ]
  );

  const columns = [
    { key: "ItemType", label: "Item Type", editable: false },
    { key: "FitmentLocation", label: "Fitment Location", editable: false },
    { key: "SKUGroup", label: "SKU Group", editable: true },
    { key: "OEMNumber", label: "OEM Number", editable: true },
    { key: "Quantity", label: "Quantity", editable: true },
    { key: "OEMNote", label: "OEM Note", editable: true },
    { key: "AdditionalFitmentInfo", label: "Additional Fitment Info", editable: true },
    { key: "FicheURL", label: "Fiche URL", editable: true } // handled specially on blur
  ];

  const navTextStyle = {
    fontSize: "0.8rem",
    lineHeight: 1.35,
    color: isDark ? "#f3f4f6" : "#1f2937"
  };
  const navTextMutedStyle = {
    ...navTextStyle,
    color: isDark ? "#9ca3af" : "#6b7280"
  };
  const navRowStyle = () => ({
    display: "flex",
    alignItems: "flex-start",
    gap: "0.65rem",
    flex: "0 0 auto"
  });
  const renderNavDetails = (meta, { labelPrefix, fallbackLabel, modelId, yearId, modelKey, align = "left" }) => {
    if (!meta) {
      return <div style={{ ...navTextMutedStyle, textAlign: align }}>{fallbackLabel}</div>;
    }

    return (
      <div style={{ ...navTextStyle, textAlign: align }}>
        <div>
          <strong>{labelPrefix}:</strong> {formatLabel(meta)}
        </div>
        {hasValue(modelId) ? <div>Model ID: {modelId}</div> : null}
        {hasValue(yearId) ? <div>Year ID: {yearId}</div> : null}
        {hasValue(modelKey) ? <div>Model Key: {modelKey}</div> : null}
      </div>
    );
  };

  const resolvingPrevious = navLookupDirection === "previous";
  const resolvingNext = navLookupDirection === "next";

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={dialogBackdropStyle}
      onMouseDown={closeOnBackdrop}
      onContextMenu={(e) => {
        // OEMAdvancedSearch disables native right-click globally; stop bubbling so context menus work here.
        e.stopPropagation();
      }}
    >
      <div
        style={dialogPanelStyle(theme)}
        onMouseDown={(e) => e.stopPropagation()}
        onContextMenu={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div style={headerStyle(theme)}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "1rem",
              flexWrap: "wrap",
              flex: "0 1 auto",
              maxWidth: "calc(100% - 120px)"
            }}
          >
            <div style={navRowStyle()}>
              <button
                type="button"
                onClick={() => handleGotoYear("previous")}
                disabled={!hasPreviousModel || resolvingPrevious}
                style={{
                  border: "none",
                  borderRadius: 6,
                  padding: "0.35rem 0.65rem",
                  cursor: hasPreviousModel && !resolvingPrevious ? "pointer" : "not-allowed",
                  background: hasPreviousModel && !resolvingPrevious ? (isDark ? "#333" : "#e6e6e6") : (isDark ? "#222" : "#f5f5f5"),
                  color: hasPreviousModel && !resolvingPrevious ? (isDark ? "#fff" : "#222") : (isDark ? "#555" : "#aaa"),
                  fontWeight: 600
                }}
              >
                {resolvingPrevious ? "Resolving…" : "← Prev Year"}
              </button>
              {renderNavDetails(previousMeta, {
                labelPrefix: "Prev",
                fallbackLabel: "No previous year available",
                modelId: previousModelId,
                yearId: previousYearId,
                modelKey: previousModelKey,
                align: "left"
              })}
            </div>
            <div style={{ flex: "0 1 auto", minWidth: "260px", margin: "0 0.25rem" }}>
              <h2 style={{ margin: 0, fontSize: "1.1rem" }}>{headerLabel}</h2>
              {currentMeta && currentMeta.region ? (
                <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>Region: {currentMeta.region}</div>
              ) : null}
              {hasValue(currentModelId) ? (
                <div style={{ fontSize: "0.85rem", opacity: 0.75 }}>Model ID: {currentModelId}</div>
              ) : null}
              {hasValue(currentYearId) ? (
                <div style={{ fontSize: "0.85rem", opacity: 0.75 }}>Year ID: {currentYearId}</div>
              ) : null}
              {hasValue(displayedModelKey) ? (
                <div style={{ fontSize: "0.85rem", opacity: 0.75 }}>Model Key: {displayedModelKey}</div>
              ) : null}
            </div>
            <div style={navRowStyle()}>
              <button
                type="button"
                onClick={() => handleGotoYear("next")}
                disabled={!hasNextModel || resolvingNext}
                style={{
                  border: "none",
                  borderRadius: 6,
                  padding: "0.35rem 0.65rem",
                  cursor: hasNextModel && !resolvingNext ? "pointer" : "not-allowed",
                  background: hasNextModel && !resolvingNext ? (isDark ? "#333" : "#e6e6e6") : (isDark ? "#222" : "#f5f5f5"),
                  color: hasNextModel && !resolvingNext ? (isDark ? "#fff" : "#222") : (isDark ? "#555" : "#aaa"),
                  fontWeight: 600
                }}
              >
                {resolvingNext ? "Resolving…" : "Next Year →"}
              </button>
              {renderNavDetails(nextMeta, {
                labelPrefix: "Next",
                fallbackLabel: "No next year available",
                modelId: nextModelId,
                yearId: nextYearId,
                modelKey: nextModelKey,
                align: "left"
              })}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flex: "0 0 auto" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
              <div ref={modelSearchWrapRef} style={{ position: "relative" }}>
                <input
                  type="text"
                  value={modelSearchQuery}
                  onChange={(e) => {
                    setModelSearchQuery(e.target.value);
                    setModelSearchOpen(true);
                  }}
                  onFocus={() => {
                    setModelSearchOpen(true);
                    if (!modelSearchQuery) {
                      fetchContextModels();
                    }
                  }}
                  placeholder="Search model…"
                  style={{ ...inputCellStyle(theme), width: 420, borderRadius: 6 }}
                />

                {modelSearchOpen ? (
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "calc(100% + 6px)",
                      width: 680,
                      maxHeight: 360,
                      overflow: "auto",
                      zIndex: 2600,
                      background: theme.surface,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 10,
                      boxShadow: "0 14px 30px rgba(0,0,0,0.25)"
                    }}
                  >
                    <div
                      style={{
                        padding: "6px 10px",
                        borderBottom: `1px solid ${theme.borderMuted}`,
                        fontSize: 12,
                        color: theme.textMuted
                      }}
                    >
                      {modelSearchLoading
                        ? "Searching…"
                        : modelSearchError
                        ? modelSearchError
                        : "Select a model to jump"}
                    </div>

                    {!modelSearchLoading && modelSearchRows.length === 0 ? (
                      <div style={{ padding: 10, fontSize: 13, color: theme.textMuted }}>No results</div>
                    ) : (
                      modelSearchRows.map((row) => {
                        const rowKey = row?.ModelKey;
                        const isCurrent = rowKey != null && String(rowKey) === String(modelKey);
                        return (
                          <div
                            key={`${row?.ModelKey}-${row?.YearID || ""}-${row?.Region || ""}-${row?.StreetName || ""}`}
                            onClick={() => jumpToModel(row)}
                            title={formatModelSearchLabel(row)}
                            style={{
                              padding: "8px 10px",
                              cursor: "pointer",
                              borderBottom: `1px solid ${theme.borderMuted}`,
                              background: isCurrent ? theme.tagBg : "transparent",
                              color: theme.text
                            }}
                          >
                            <div style={{ fontSize: 13, fontWeight: 600 }}>
                              {formatModelSearchLabel(row)}
                            </div>
                            <div style={{ fontSize: 12, opacity: 0.85 }}>
                              ModelKey: {row?.ModelKey}
                              {row?.ModelID ? ` • ModelID: ${row.ModelID}` : ""}
                              {row?.YearID ? ` • YearID: ${row.YearID}` : ""}
                              {row?.EngineType ? ` • ${row.EngineType}` : ""}
                              {row?.VehicleType ? ` • ${row.VehicleType}` : ""}
                              {row?.EngineCC ? ` • ${row.EngineCC}cc` : ""}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : null}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: 8,
                  alignItems: "center",
                  width: 420
                }}
              >
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (backRecentTargetIndex == null) return;
                      navigateToNavStackIndex(backRecentTargetIndex);
                    }}
                    disabled={!canGoBackRecent}
                    title="Back to previous model"
                    style={{
                      border: `1px solid ${theme.border}`,
                      background: theme.toolbarBg,
                      color: theme.text,
                      borderRadius: 8,
                      padding: "0.25rem 0.55rem",
                      cursor: canGoBackRecent ? "pointer" : "not-allowed",
                      opacity: canGoBackRecent ? 1 : 0.55,
                      fontWeight: 700
                    }}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (forwardRecentTargetIndex == null) return;
                      navigateToNavStackIndex(forwardRecentTargetIndex);
                    }}
                    disabled={!canGoForwardRecent}
                    title="Forward to next model"
                    style={{
                      border: `1px solid ${theme.border}`,
                      background: theme.toolbarBg,
                      color: theme.text,
                      borderRadius: 8,
                      padding: "0.25rem 0.55rem",
                      cursor: canGoForwardRecent ? "pointer" : "not-allowed",
                      opacity: canGoForwardRecent ? 1 : 0.55,
                      fontWeight: 700
                    }}
                  >
                    →
                  </button>
                </div>

                <select
                  value={recentDropdownValue}
                  onChange={(e) => {
                    const key = e.target.value;
                    if (!key) return;
                    const entry = recentModels.find((m) => String(m.modelKey) === String(key));
                    if (!entry?.modelKey) return;
                    jumpToModel(entry.meta || { ModelKey: entry.modelKey, modelKey: entry.modelKey });
                  }}
                  disabled={!recentModels.length}
                  title="Recently visited models"
                  style={{
                    ...inputCellStyle(theme),
                    borderRadius: 8,
                    padding: "6px 10px",
                    opacity: recentModels.length ? 1 : 0.7
                  }}
                >
                  {!recentModels.length ? (
                    <option value="">No recent models</option>
                  ) : (
                    recentModels.map((entry) => (
                      <option key={`${entry.modelKey}`} value={String(entry.modelKey)}>
                        {entry.label} — {entry.modelKey}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <button
              onClick={onClose}
              style={{
                border: "none",
                borderRadius: 6,
                padding: "0.5rem 1rem",
                cursor: "pointer",
                background: isDark ? "#444" : "#dcdcdc",
                color: isDark ? "#fff" : "#222",
                fontWeight: 600
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div style={toolbarStyle(theme)}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.25rem 0.5rem",
              borderRadius: 6,
              background: theme.prevNextBg,
              border: `1px solid ${theme.border}`,
              color: theme.text
            }}
            title="Press F2 to toggle"
          >
            <input
              type="checkbox"
              checked={!!showPrevNext}
              onChange={(e) => setShowPrevNext(!!e.target.checked)}
            />
            Show previous/next year data <span style={{ opacity: 0.6, fontSize: "0.85em" }}>(F2)</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <input
              type="checkbox"
              checked={!!formatOEMEnabled}
              onChange={(e) => updateFormatToggle(!!e.target.checked)}
            />
            Auto-format OEM Number on entry
          </label>
          <label
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            title="Press F3 to toggle"
          >
            <input
              type="checkbox"
              checked={!!hideCompleted}
              onChange={(e) => setHideCompleted(!!e.target.checked)}
            />
            Hide completed lines <span style={{ opacity: 0.6, fontSize: "0.85em" }}>(F3)</span>
          </label>
          <span
            style={{
              fontSize: "0.9rem",
              opacity: 0.85,
              display: "flex",
              alignItems: "center",
              gap: "0.35rem",
              flexWrap: "wrap"
            }}
          >
            <span>Completed —</span>
            {completedSummaryEntries.map((entry, idx) => (
              <React.Fragment key={entry.key}>
                {idx > 0 && <span style={{ opacity: 0.6 }}>·</span>}
                <span
                  style={{
                    fontWeight: entry.isActive ? 600 : 400,
                    color: entry.isActive ? theme.text : theme.textMuted
                  }}
                >
                  {entry.label}: {entry.value}
                </span>
              </React.Fragment>
            ))}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.85rem", opacity: 0.75 }}>
              {selectionSummary}
            </span>
            <button
              onClick={handleOpenManageOEM}
              disabled={!selectedManageRow || !canWrite}
              title={manageOEMButtonTitle}
              style={{
                border: "none",
                borderRadius: 6,
                padding: "0.4rem 0.8rem",
                cursor: selectedManageRow && canWrite ? "pointer" : "not-allowed",
                background:
                  selectedManageRow && canWrite
                    ? isDark
                      ? "#3b82f6"
                      : "#2563eb"
                    : isDark
                    ? "#555"
                    : "#bbb",
                color: selectedManageRow && canWrite ? "#fff" : (isDark ? "#999" : "#444")
              }}
            >
              Manage OEM
            </button>
            <button
              onClick={handleOpenManageSKU}
              disabled={!selectedManageRow || !canWrite}
              title={manageSKUButtonTitle}
              style={{
                border: "none",
                borderRadius: 6,
                padding: "0.4rem 0.8rem",
                cursor: selectedManageRow && canWrite ? "pointer" : "not-allowed",
                background:
                  selectedManageRow && canWrite
                    ? isDark
                      ? "#6366f1"
                      : "#4338ca"
                    : isDark
                    ? "#555"
                    : "#bbb",
                color: selectedManageRow && canWrite ? "#fff" : (isDark ? "#999" : "#444")
              }}
            >
              Manage SKU
            </button>
            <button
              onClick={handleOpenAddOneOff}
              disabled={!canWrite || !selectedManageRow}
              title={addOneOffButtonTitle}
              style={{
                border: "none",
                borderRadius: 6,
                padding: "0.4rem 0.8rem",
                cursor:
                  canWrite && selectedManageRow ? "pointer" : "not-allowed",
                background:
                  canWrite && selectedManageRow
                    ? isDark
                      ? "#10b981"
                      : "#059669"
                    : isDark
                    ? "#555"
                    : "#bbb",
                color:
                  canWrite && selectedManageRow
                    ? "#fff"
                    : isDark
                    ? "#999"
                    : "#444"
              }}
            >
              Add One-Off
            </button>
            <button
              onClick={handleOpenKitFromSelection}
              disabled={!canManageKits || selectedKitRowIds.size === 0}
              title={
                !canManageKits
                  ? "You do not have permission to manage kits"
                  : selectedKitRowIds.size === 0
                  ? "Tick one or more current-year lines to build a kit from"
                  : `Create a kit from the ${selectedKitRowIds.size} selected line(s)`
              }
              style={{
                border: "none",
                borderRadius: 6,
                padding: "0.4rem 0.8rem",
                cursor:
                  canManageKits && selectedKitRowIds.size > 0 ? "pointer" : "not-allowed",
                background:
                  canManageKits && selectedKitRowIds.size > 0
                    ? isDark
                      ? "#f59e0b"
                      : "#d97706"
                    : isDark
                    ? "#555"
                    : "#bbb",
                color:
                  canManageKits && selectedKitRowIds.size > 0
                    ? "#fff"
                    : isDark
                    ? "#999"
                    : "#444"
              }}
            >
              Create Kit from Selected ({selectedKitRowIds.size})
            </button>
          </div>
          {showPrevNext && canWrite ? (
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                onClick={() => copyModelFromSection("previous")}
                disabled={copyingSection === "previous" || !data?.previous?.rows?.length}
                style={{
                  border: "none",
                  borderRadius: 6,
                  padding: "0.4rem 0.8rem",
                  cursor: data?.previous?.rows?.length ? "pointer" : "not-allowed",
                  background: copyingSection === "previous" ? "#999" : "#2563eb",
                  color: "#fff"
                }}
              >
                {copyingSection === "previous" ? "Copying…" : "Copy Prev Year → Current"}
              </button>
              <button
                onClick={() => copyModelFromSection("next")}
                disabled={copyingSection === "next" || !data?.next?.rows?.length}
                style={{
                  border: "none",
                  borderRadius: 6,
                  padding: "0.4rem 0.8rem",
                  cursor: data?.next?.rows?.length ? "pointer" : "not-allowed",
                  background: copyingSection === "next" ? "#999" : "#2563eb",
                  color: "#fff"
                }}
              >
                {copyingSection === "next" ? "Copying…" : "Copy Next Year → Current"}
              </button>
            </div>
          ) : null}
          {loading ? <span>Loading…</span> : null}
          {error ? <span style={{ color: "#d14343" }}>{error}</span> : null}
        </div>

        {/* Body */}
        <div style={bodyContainerStyle(theme)}>
          <div style={tableWrapStyle}>
            <table style={tableStyle(theme)}>
              <thead>
                <tr>
                  <th style={{ ...thSticky(theme), textAlign: "center", width: 34 }}>
                    <input
                      type="checkbox"
                      checked={allCurrentSelected}
                      onChange={toggleSelectAllCurrent}
                      disabled={selectableCurrentIds.length === 0}
                      title="Select/clear all visible current-year lines"
                    />
                  </th>
                  {columns.map((c) => (
                    <th key={c.key} style={thSticky(theme)}>{c.label}</th>
                  ))}
                  <th style={thSticky(theme)}>Status</th>
                  <th style={{ ...thSticky(theme), textAlign: "center" }}>Actions</th>
                </tr>
                <tr>
                  <th style={thFilter(theme)} />
                  {columns.map((c) => (
                    <th key={c.key} style={thFilter(theme)}>
                      <input
                        value={filters[c.key]}
                        onChange={(e) =>
                          setFilters((f) => ({ ...f, [c.key]: e.target.value }))
                        }
                        placeholder="Filter…"
                        style={inputCellStyle(theme)}
                      />
                    </th>
                  ))}
                  <th style={thFilter(theme)} />
                  <th style={thFilter(theme)} />
                </tr>
              </thead>

              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 3} style={{ padding: "0.75rem", textAlign: "center", opacity: 0.6 }}>
                      No rows to display.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row, idx) => {
                    const prevRow = idx > 0 ? filteredRows[idx - 1] : null;
                    const isGhost = !!(row.IsGhost || row.isGhost);
                    const statusBits = [];
                    if (row.IsOneOff) statusBits.push("One-Off");
                    if (isGhost) statusBits.push(row.IsCurrent ? "Ghost" : "Ghost (sibling)");
                    const prevNextLabel =
                      !row.IsCurrent && (row._section === "previous" || row._section === "next")
                        ? `${row._section === "previous" ? "Previous Year" : "Next Year"}${
                            row.Year !== null && row.Year !== undefined ? ` (${row.Year})` : ""
                          }`
                        : "";
                    if (prevNextLabel) statusBits.push(prevNextLabel);
                    const statusText = statusBits.length
                      ? statusBits.join(" • ")
                      : row.IsCurrent
                      ? "Active"
                      : prevNextLabel
                      ? prevNextLabel
                      : "Sibling";
                    const rowIdentity = rowIdentityKey(row);
                    const sectionLabel = row._section || (row.IsCurrent ? "current" : "sibling");
                    const copyKey =
                      String(sectionLabel) +
                      "-" +
                      String(row.FitmentLocationKey) +
                      "-" +
                      String(row.ItemTypeKey);
                    const rowKey = rowIdentity || copyKey || String(idx);
                    const isCurrent = !!row.IsCurrent;
                    const kitId = extractKitId(row);
                    const directKitId = row.KitId ?? row.KitID ?? null;
                    const inheritedKitId = !directKitId ? row.LinkedKitId ?? null : null;
                    const normalizedOEMNumber = typeof row.OEMNumber === 'string' ? row.OEMNumber.trim() : '';
                    const oemLooksLikeKit = normalizedOEMNumber.toUpperCase().startsWith(KIT_ID_PREFIX);
                    const ownsKit = Boolean(
                      directKitId || (!inheritedKitId && (oemLooksLikeKit || kitId))
                    );
                    const resolvedKitLabel = kitId || normalizedOEMNumber || inheritedKitId || '';
                    const hasFitment = !!row.FitmentKey;
                    const kitButtonDisabled = !canManageKits;
                    const kitButtonTitle = kitButtonDisabled
                      ? "You do not have permission to manage kits."
                      : ownsKit
                      ? (resolvedKitLabel ? `Manage Kit ${resolvedKitLabel}` : 'Manage kit for this row')
                      : inheritedKitId
                      ? `This row is using kit ${inheritedKitId || kitId}. Choose New Kit to create a fitment-specific kit.`
                      : hasFitment
                      ? "Create or edit a kit for this fitment."
                      : "Create a kit for this row (saves the fitment automatically).";
                    const kitButtonStyles = {
                      border: kitButtonDisabled
                        ? "1px solid #d1d5db"
                        : ownsKit
                        ? "1px solid #312e81"
                        : "1px solid #bfdbfe",
                      borderRadius: 6,
                      padding: "0.35rem 0.75rem",
                      cursor: kitButtonDisabled ? "not-allowed" : "pointer",
                      background: kitButtonDisabled
                        ? isDark
                          ? "#2d2d2d"
                          : "#f3f4f6"
                        : ownsKit
                        ? isDark
                          ? "#4338ca"
                          : "#2563eb"
                        : isDark
                        ? "#1f2937"
                        : "#eff6ff",
                      color: kitButtonDisabled
                        ? isDark
                          ? "#6b7280"
                          : "#9ca3af"
                        : ownsKit
                        ? "#fff"
                        : isDark
                        ? "#dbeafe"
                        : "#1e3a8a",
                      minWidth: 88
                    };
                    const kitButtonLabel = ownsKit ? "Manage Kit" : "New Kit";
                    const isSelected = rowIdentity === selectedCell.rowIdentity;
                    const combinedRowStyle = isSelected
                      ? {
                          ...rowStyle(row, prevRow, theme),
                          outline: `2px solid ${isDark ? "#818cf8" : "#3b82f6"}`,
                          outlineOffset: -2
                        }
                      : rowStyle(row, prevRow, theme);

                    return (
                      <tr
                        key={rowKey}
                        style={combinedRowStyle}
                        onMouseDown={(e) => {
                          if (typeof e.target.closest === "function" && e.target.closest("button")) return;
                          handleSelectRow(rowIdentity);
                        }}
                      >
                        <td
                          style={{ ...tdCell(theme), textAlign: "center", width: 34 }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {isCurrent ? (
                            <input
                              type="checkbox"
                              checked={selectedKitRowIds.has(rowIdentity)}
                              onChange={() => toggleKitRowSelection(rowIdentity)}
                              title="Select this line to include its OEM in a new kit"
                            />
                          ) : null}
                        </td>
                        {columns.map((c) => {
                          const val = valueByKey(row, c.key);
                          const editableHere = isCurrent && canWrite && c.editable;

                          if (editableHere) {
                            const lockedUntilOem =
                              (c.key === "SKUGroup" || c.key === "Quantity") &&
                              !String(row.OEMNumber ?? "").trim();
                            return (
                              <td key={c.key} style={tdCell(theme)}>
                                <input
                                  value={
                                    editing.rowIdentity === rowIdentity && editing.field === c.key
                                      ? valueToString(editing.value)
                                      : valueToString(val)
                                  }
                                  onChange={(e) => handleChange(rowIdentity, c.key, e.target.value)}
                                  onBlur={() => handleBlur(rowIdentity, c.key)}
                                  onKeyDown={(e) => handleKeyDown(e, rowIdentity, c.key)}
                                  onFocus={() => handleSelectCell(rowIdentity, c.key)}
                                  data-row-identity={rowIdentity}
                                  data-field={c.key}
                                  readOnly={lockedUntilOem}
                                  placeholder={lockedUntilOem ? "OEM first" : undefined}
                                  title={lockedUntilOem ? "Enter an OEM number first" : undefined}
                                  style={lockedUntilOem
                                    ? { ...inputCellStyle(theme), opacity: 0.5, cursor: "not-allowed" }
                                    : inputCellStyle(theme)}
                                />
                              </td>
                            );
                          }

                          return (
                            <td key={c.key} style={tdCell(theme)} onClick={() => handleSelectRow(rowIdentity)}>
                              {normalizeCopyField(val) || <span style={{ opacity: 0.6 }}>—</span>}
                            </td>
                          );
                        })}

                        <td style={tdCell(theme)}>{statusText}</td>

                        <td style={tdCellActions(theme)}>
                          {!isCurrent ? (
                            <button
                              onClick={() => copyFromRow(row)}
                              disabled={!canWrite || copyingKey === copyKey}
                              style={{
                                border: "none",
                                borderRadius: 6,
                                padding: "0.4rem 0.75rem",
                                cursor: canWrite ? "pointer" : "not-allowed",
                                background: canWrite ? "#2563eb" : "#999",
                                color: "#fff",
                                opacity: copyingKey === copyKey ? 0.7 : 1
                              }}
                            >
                              {copyingKey === copyKey ? "Copying…" : "Copy to current"}
                            </button>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                gap: "0.5rem"
                              }}
                            >
                                <button
                                  onClick={() => openKitModalForRow(row)}
                                  disabled={kitButtonDisabled}
                                  title={kitButtonTitle}
                                  style={kitButtonStyles}
                                >
                                  {kitButtonLabel}
                                </button>
                              <button
                                onClick={() => openHistory(row.FitmentKey)}
                                disabled={!row.FitmentKey}
                                style={{
                                  border: `1px solid ${theme.border}`,
                                  borderRadius: 6,
                                  padding: "0.35rem 0.75rem",
                                  cursor: row.FitmentKey ? "pointer" : "not-allowed",
                                  background: row.FitmentKey ? theme.tagBg : theme.toolbarBg,
                                  color: row.FitmentKey ? theme.tagText : theme.textMuted,
                                  minWidth: 88,
                                  opacity: row.FitmentKey ? 1 : 0.6
                                }}
                              >
                                {historyLoading && historyFitmentKey === row.FitmentKey
                                  ? "Loading…"
                                  : "History"}
                              </button>
                              <span style={{ fontSize: "0.85rem", opacity: 0.7 }}>
                                {isGhost ? "Ghost (enter values to create)" : "Editable"}
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {historyVisible && (
            <div style={historyOverlayStyle} onMouseDown={closeHistory}>
              <div style={historyPanelStyle(theme)} onMouseDown={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <h3 style={{ margin: 0, fontSize: "1rem" }}>
                    History for Fitment {historyFitmentKey}
                  </h3>
                  <button
                    onClick={closeHistory}
                    style={{
                      border: `1px solid ${theme.border}`,
                      background: theme.toolbarBg,
                      color: theme.text,
                      padding: "0.35rem 0.65rem",
                      borderRadius: 6,
                      cursor: "pointer"
                    }}
                  >
                    Close
                  </button>
                </div>
                {historyLoading ? <div>Loading history…</div> : null}
                {historyError ? (
                  <div style={{ color: "#dc2626", marginBottom: "0.5rem" }}>{historyError}</div>
                ) : null}
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", borderBottom: `1px solid ${theme.border}`, padding: "0.35rem" }}>Time</th>
                      <th style={{ textAlign: "left", borderBottom: `1px solid ${theme.border}`, padding: "0.35rem" }}>User</th>
                      <th style={{ textAlign: "left", borderBottom: `1px solid ${theme.border}`, padding: "0.35rem" }}>Field</th>
                      <th style={{ textAlign: "left", borderBottom: `1px solid ${theme.border}`, padding: "0.35rem" }}>Old</th>
                      <th style={{ textAlign: "left", borderBottom: `1px solid ${theme.border}`, padding: "0.35rem" }}>New</th>
                      <th style={{ textAlign: "center", borderBottom: `1px solid ${theme.border}`, padding: "0.35rem" }}>Undo</th>
                      <th style={{ textAlign: "center", borderBottom: `1px solid ${theme.border}`, padding: "0.35rem" }}>Redo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRecords.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: "0.75rem", textAlign: "center", opacity: 0.7 }}>
                          No history records.
                        </td>
                      </tr>
                    ) : (
                      historyRecords.map((h, i) => (
                        <tr key={i}>
                          <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}` }}>
                            {h.ChangeTime ? new Date(h.ChangeTime).toLocaleString() : "—"}
                          </td>
                          <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}` }}>{h.ChangedBy || "—"}</td>
                          <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}` }}>{h.FieldName || "—"}</td>
                          <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}` }}>{h.OldValue || ""}</td>
                          <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}` }}>{h.NewValue || ""}</td>
                          <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}`, textAlign: "center" }}>
                            <button
                              onClick={() => handleHistoryUndo(h)}
                              disabled={!canWrite}
                              title={canWrite ? "Undo change" : "Requires write permission"}
                              style={{
                                border: `1px solid ${theme.border}`,
                                borderRadius: 6,
                                padding: "0.25rem 0.6rem",
                                cursor: canWrite ? "pointer" : "not-allowed",
                                background: canWrite ? theme.tagBg : theme.toolbarBg,
                                color: canWrite ? theme.tagText : theme.textMuted,
                                opacity: canWrite ? 1 : 0.6
                              }}
                            >
                              Undo
                            </button>
                          </td>
                          <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}`, textAlign: "center" }}>
                            <button
                              onClick={() => handleHistoryRedo(h)}
                              disabled={!canWrite}
                              title={canWrite ? "Redo change" : "Requires write permission"}
                              style={{
                                border: `1px solid ${theme.border}`,
                                borderRadius: 6,
                                padding: "0.25rem 0.6rem",
                                cursor: canWrite ? "pointer" : "not-allowed",
                                background: canWrite ? theme.tagBg : theme.toolbarBg,
                                color: canWrite ? theme.tagText : theme.textMuted,
                                opacity: canWrite ? 1 : 0.6
                              }}
                            >
                              Redo
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {navChoice && (
            <div style={navChoiceOverlayStyle} onMouseDown={handleNavChoiceCancel}>
              <div style={navChoicePanelStyle(theme)} onMouseDown={(e) => e.stopPropagation()}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "1rem",
                    flexWrap: "wrap"
                  }}
                >
                  {(() => {
                    const isCopyMode = navChoice.mode === "copy";
                    const directionText = navChoice.direction === "previous" ? "previous" : "next";
                    const title = isCopyMode
                      ? `Choose ${directionText} model to copy`
                      : `Multiple ${directionText} options found`;
                    const description = isCopyMode
                      ? "Select the exact model whose data should be copied into the current year."
                      : "This model has more than one entry sharing Make, Model, Street, Year, and Region. Select the variant with the Model ID / Year ID you need.";
                    return (
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ margin: 0 }}>{title}</h3>
                        <p style={{ margin: "0.35rem 0", fontSize: "0.9rem", opacity: 0.8 }}>
                          {description}
                        </p>
                      </div>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={handleNavChoiceCancel}
                    style={{
                      border: `1px solid ${theme.border}`,
                      borderRadius: 6,
                      padding: "0.35rem 0.75rem",
                      cursor: "pointer",
                      background: theme.toolbarBg,
                      color: theme.text
                    }}
                  >
                    Cancel
                  </button>
                </div>
                <div style={{ overflowX: "auto" }}>
                  {(() => {
                    const columns = [
                      { key: "__origin", label: "Source" },
                      { key: "Make", label: "Make" },
                      { key: "ModelName", label: "Model" },
                      { key: "StreetName", label: "Street" },
                      { key: "Year", label: "Year" },
                      { key: "Region", label: "Region" },
                      { key: "ModelID", label: "Model ID" },
                      { key: "YearID", label: "Year ID" },
                      { key: "ModelKey", label: "Model Key" }
                    ];
                    const diffMap = columns.reduce((acc, col) => {
                      if (col.key === "__origin") {
                        acc[col.key] = true;
                        return acc;
                      }
                      const values = navChoice.options.map((opt) => normalizeLower(opt[col.key] ?? ""));
                      acc[col.key] = new Set(values).size > 1;
                      return acc;
                    }, {});
                    return (
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                        <thead>
                          <tr>
                            {columns.map((col) => (
                              <th
                                key={col.key}
                                style={{
                                  textAlign: "left",
                                  padding: "0.4rem",
                                  borderBottom: `1px solid ${theme.border}`
                                }}
                              >
                                {col.label}
                              </th>
                            ))}
                            <th style={{ textAlign: "right", padding: "0.4rem", borderBottom: `1px solid ${theme.border}` }}>
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {navChoice.options.map((option, idx) => (
                            <tr key={option.ModelKey ?? `${option.ModelID}-${option.YearID}-${idx}`}>
                              {columns.map((col) => (
                                <td
                                  key={`${col.key}-${idx}`}
                                  style={{
                                    padding: "0.4rem",
                                    borderBottom: `1px solid ${theme.rowDivider}`,
                                    fontWeight: diffMap[col.key] ? 600 : 400
                                  }}
                                >
                                  {col.key === "__origin"
                                    ? option.__origin || "—"
                                    : hasValue(option[col.key])
                                    ? option[col.key]
                                    : "—"}
                                </td>
                              ))}
                              <td
                                style={{
                                  padding: "0.4rem",
                                  borderBottom: `1px solid ${theme.rowDivider}`,
                                  textAlign: "right"
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => handleNavChoiceSelect(option)}
                                  style={{
                                    border: "none",
                                    borderRadius: 6,
                                    padding: "0.35rem 0.85rem",
                                    cursor: "pointer",
                                    background: "#2563eb",
                                    color: "#fff"
                                  }}
                                >
                                  Select
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
        {bulkCopyReview && (
          <div style={bulkCopyOverlayStyle} onMouseDown={handleBulkCopyReviewCancel}>
            <div style={bulkCopyPanelStyle(theme)} onMouseDown={(e) => e.stopPropagation()}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "1rem",
                  flexWrap: "wrap"
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  <h3 style={{ margin: 0, fontSize: "1.05rem" }}>
                    Review copy from {bulkCopyReview.directionLabel || "source"}
                  </h3>
                  <span style={{ fontSize: "0.85rem", opacity: 0.8 }}>
                    {bulkCopyTotals.conflicts} conflict{bulkCopyTotals.conflicts === 1 ? "" : "s"}; {bulkCopyTotals.extras} additional row{bulkCopyTotals.extras === 1 ? "" : "s"}
                  </span>
                  {bulkCopyReview.duplicatesSkipped ? (
                    <span style={{ fontSize: "0.8rem", opacity: 0.75 }}>
                      {bulkCopyReview.duplicatesSkipped} identical row{bulkCopyReview.duplicatesSkipped === 1 ? " was" : "s were"} skipped automatically.
                    </span>
                  ) : null}
                  {bulkCopyReview.noteMarker ? (
                    <span style={{ fontSize: "0.8rem", opacity: 0.75 }}>
                      Copy notes will include: {bulkCopyReview.noteMarker}
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={handleBulkCopyReviewCancel}
                  style={{
                    border: `1px solid ${theme.border}`,
                    borderRadius: 6,
                    padding: "0.4rem 0.75rem",
                    cursor: "pointer",
                    background: theme.toolbarBg,
                    color: theme.text
                  }}
                >
                  Cancel
                </button>
              </div>

              {bulkCopyTotals.total === 0 ? (
                <div style={{ padding: "1rem", textAlign: "center", opacity: 0.8 }}>
                  Nothing to review—close this dialog to continue.
                </div>
              ) : null}

              {bulkCopyReview.conflicts?.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  <h4 style={{ margin: 0 }}>Conflicts</h4>
                  <p style={{ margin: 0, fontSize: "0.85rem", opacity: 0.75 }}>
                    These rows already have data in the current model. Choose whether to override, add a duplicate row, or ignore each one.
                  </p>
                  <div style={bulkCopyCardGridStyle}>
                    {bulkCopyReview.conflicts.map((item) => {
                    const decision = bulkCopyDecisions[item.id] || item.defaultDecision || "override";
                    const neutralBorder = theme.cardBorder;
                    const tableBorder = theme.rowDivider;
                    const cardBackground = theme.cardBg;
                    const radioOptions = [
                      {
                        value: "override",
                        label: "Override current",
                        hint: "Replace the current row with incoming values."
                      },
                      {
                        value: "add",
                        label: "Add new row",
                        hint: "Keep the existing row and create an additional one."
                      },
                      {
                        value: "ignore",
                        label: "Ignore",
                        hint: "Skip this row and leave it unchanged."
                      }
                    ];

                      return (
                        <div
                        key={item.id}
                        style={{
                          border: `1px solid ${neutralBorder}`,
                          borderRadius: 8,
                          padding: "0.75rem",
                          background: cardBackground,
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.6rem"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "1rem",
                            flexWrap: "wrap"
                          }}
                        >
                          <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <span style={{ fontWeight: 600 }}>{item.itemType || "—"}</span>
                            <span style={{ fontSize: "0.85rem" }}>{item.fitmentLocation || "—"}</span>
                            {item.additionalInfo ? (
                              <span style={{ fontSize: "0.8rem", opacity: 0.75 }}>{item.additionalInfo}</span>
                            ) : null}
                          </div>
                          <div style={bulkCopyRadioGridStyle}>
                            {radioOptions.map((option) => {
                              const selected = decision === option.value;
                              return (
                                <label
                                  key={option.value}
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.2rem",
                                    padding: "0.4rem 0.55rem",
                                    borderRadius: 6,
                                    border: `1px solid ${selected ? "#2563eb" : neutralBorder}`,
                                    background: selected ? theme.tagBg : "transparent",
                                    color: selected ? theme.tagText : theme.text
                                  }}
                                >
                                  <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                    <input
                                      type="radio"
                                      name={`bulk-decision-${item.id}`}
                                      value={option.value}
                                      checked={decision === option.value}
                                      onChange={() => updateBulkCopyDecision(item.id, option.value)}
                                    />
                                    <span>{option.label}</span>
                                  </span>
                                  <span style={{ fontSize: "0.75rem", opacity: 0.75 }}>{option.hint}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        <table
                          style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  textAlign: "left",
                                  borderBottom: `1px solid ${tableBorder}`,
                                  padding: "0.35rem"
                                }}
                              >
                                Field
                              </th>
                              <th
                                style={{
                                  textAlign: "left",
                                  borderBottom: `1px solid ${tableBorder}`,
                                  padding: "0.35rem"
                                }}
                              >
                                Current value
                              </th>
                              <th
                                style={{
                                  textAlign: "left",
                                  borderBottom: `1px solid ${tableBorder}`,
                                  padding: "0.35rem"
                                }}
                              >
                                Incoming value
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.differences.map((diff, idx) => (
                              <tr key={`${item.id}-${diff.field}-${idx}`}>
                                <td
                                  style={{
                                    padding: "0.4rem",
                                    borderBottom: `1px solid ${tableBorder}`,
                                    whiteSpace: "nowrap"
                                  }}
                                >
                                  {diff.field}
                                </td>
                                <td
                                  style={{ padding: "0.4rem", borderBottom: `1px solid ${tableBorder}` }}
                                >
                                  {diff.currentValue}
                                </td>
                                <td
                                  style={{ padding: "0.4rem", borderBottom: `1px solid ${tableBorder}` }}
                                >
                                  {diff.incomingValue}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {bulkCopyReview.extras?.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  <h4 style={{ margin: 0 }}>Additional rows</h4>
                  <p style={{ margin: 0, fontSize: "0.85rem", opacity: 0.75 }}>
                    These rows do not have a matching current fitment. Choose whether to add them as new rows or ignore them.
                  </p>
                  <div style={bulkCopyCardGridStyle}>
                    {bulkCopyReview.extras.map((item) => {
                    const decision = bulkCopyDecisions[item.id] || item.defaultDecision || "add";
                    const neutralBorder = theme.cardBorder;
                    const tableBorder = theme.rowDivider;
                    const cardBackground = theme.cardBg;
                    const radioOptions = [
                      {
                        value: "add",
                        label: "Add as new row",
                        hint: "Create a new row in the current model with these values."
                      },
                      {
                        value: "ignore",
                        label: "Ignore",
                        hint: "Skip this row for now."
                      }
                    ];

                      return (
                        <div
                        key={item.id}
                        style={{
                          border: `1px solid ${neutralBorder}`,
                          borderRadius: 8,
                          padding: "0.75rem",
                          background: cardBackground,
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.6rem"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "1rem",
                            flexWrap: "wrap"
                          }}
                        >
                          <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                            <span style={{ fontWeight: 600 }}>{item.itemType || "—"}</span>
                            <span style={{ fontSize: "0.85rem" }}>{item.fitmentLocation || "—"}</span>
                            {item.additionalInfo ? (
                              <span style={{ fontSize: "0.8rem", opacity: 0.75 }}>{item.additionalInfo}</span>
                            ) : null}
                          </div>
                          <div style={bulkCopyRadioGridStyle}>
                            {radioOptions.map((option) => {
                              const selected = decision === option.value;
                              return (
                                <label
                                  key={option.value}
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.2rem",
                                    padding: "0.4rem 0.55rem",
                                    borderRadius: 6,
                                    border: `1px solid ${selected ? "#2563eb" : neutralBorder}`,
                                    background: selected ? theme.tagBg : "transparent",
                                    color: selected ? theme.tagText : theme.text
                                  }}
                                >
                                  <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                    <input
                                      type="radio"
                                      name={`bulk-decision-${item.id}`}
                                      value={option.value}
                                      checked={decision === option.value}
                                      onChange={() => updateBulkCopyDecision(item.id, option.value)}
                                    />
                                    <span>{option.label}</span>
                                  </span>
                                  <span style={{ fontSize: "0.75rem", opacity: 0.75 }}>{option.hint}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        <table
                          style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  textAlign: "left",
                                  borderBottom: `1px solid ${tableBorder}`,
                                  padding: "0.35rem"
                                }}
                              >
                                Field
                              </th>
                              <th
                                style={{
                                  textAlign: "left",
                                  borderBottom: `1px solid ${tableBorder}`,
                                  padding: "0.35rem"
                                }}
                              >
                                Value to copy
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.updates.map((update, idx) => (
                              <tr key={`${item.id}-${update.field}-${idx}`}>
                                <td
                                  style={{
                                    padding: "0.4rem",
                                    borderBottom: `1px solid ${tableBorder}`,
                                    whiteSpace: "nowrap"
                                  }}
                                >
                                  {update.field}
                                </td>
                                <td
                                  style={{ padding: "0.4rem", borderBottom: `1px solid ${tableBorder}` }}
                                >
                                  {displayValue(update.field, update.value) || "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.75rem",
                  paddingTop: "0.5rem",
                  borderTop: `1px solid ${isDark ? "#3a3a3a" : "#e5e7eb"}`
                }}
              >
                <button
                  type="button"
                  onClick={handleBulkCopyReviewCancel}
                  style={{
                    border: "none",
                    borderRadius: 6,
                    padding: "0.45rem 0.85rem",
                    cursor: "pointer",
                    background: isDark ? "#3a3a3a" : "#e5e7eb",
                    color: isDark ? "#f3f4f6" : "#111827"
                  }}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={confirmBulkCopyReview}
                  disabled={!canConfirmBulkCopy}
                  style={{
                    border: "none",
                    borderRadius: 6,
                    padding: "0.45rem 0.9rem",
                    cursor: canConfirmBulkCopy ? "pointer" : "not-allowed",
                    background: canConfirmBulkCopy ? "#2563eb" : "#9aa5b1",
                    color: "#fff",
                    opacity: canConfirmBulkCopy ? 1 : 0.7
                  }}
                >
                  Apply selections
                </button>
              </div>
            </div>
          </div>
        )}
        {kitBuilder && (
          <div style={navChoiceOverlayStyle} onMouseDown={() => setKitBuilder(null)}>
            <div style={navChoicePanelStyle(theme)} onMouseDown={(e) => e.stopPropagation()}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "1rem",
                  flexWrap: "wrap"
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ margin: 0 }}>Create Kit from Selected Lines</h3>
                  <p style={{ margin: "0.35rem 0", fontSize: "0.9rem", opacity: 0.8 }}>
                    {kitBuilder.components.length} OEM component
                    {kitBuilder.components.length === 1 ? "" : "s"} will be added to a new kit.
                    Choose which Item Type / Fitment Location the Kit number should be inserted
                    into, then continue to assign the Kit's SKU Group.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setKitBuilder(null)}
                  style={{
                    border: `1px solid ${theme.border}`,
                    borderRadius: 6,
                    padding: "0.35rem 0.75rem",
                    cursor: "pointer",
                    background: theme.toolbarBg,
                    color: theme.text
                  }}
                >
                  Cancel
                </button>
              </div>

              <div
                style={{
                  maxHeight: "38vh",
                  overflowY: "auto",
                  border: `1px solid ${theme.cardBorder}`,
                  borderRadius: 8
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                  <thead>
                    <tr>
                      {["OEM Number", "Item Type", "Fitment Location", "Qty", "SKU Group"].map(
                        (h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: "left",
                              padding: "0.4rem",
                              borderBottom: `1px solid ${theme.border}`,
                              position: "sticky",
                              top: 0,
                              background: theme.tableHeaderBg
                            }}
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {kitBuilder.components.map((comp, idx) => (
                      <tr key={`${comp.ComponentOEMNumber}-${idx}`}>
                        <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}` }}>
                          {comp.ComponentOEMNumber}
                        </td>
                        <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}` }}>
                          {comp.ItemType || "—"}
                        </td>
                        <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}` }}>
                          {comp.FitmentLocation || "—"}
                        </td>
                        <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}` }}>
                          {comp.Quantity}
                        </td>
                        <td style={{ padding: "0.4rem", borderBottom: `1px solid ${theme.rowDivider}` }}>
                          {comp.SKUGroup || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <label
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.35rem",
                  fontSize: "0.9rem"
                }}
              >
                Insert Kit number into (Item Type / Fitment Location):
                <select
                  value={kitBuilder.targetIdentity}
                  onChange={(e) =>
                    setKitBuilder((prev) =>
                      prev ? { ...prev, targetIdentity: e.target.value } : prev
                    )
                  }
                  style={{ ...inputCellStyle(theme), borderRadius: 6, padding: "6px 10px" }}
                >
                  <option value="">— Select a current-year line —</option>
                  {kitBuilderTargetOptions.map((opt) => (
                    <option key={opt.identity} value={opt.identity}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                <button
                  type="button"
                  onClick={() => setKitBuilder(null)}
                  style={{
                    border: "none",
                    borderRadius: 6,
                    padding: "0.45rem 0.85rem",
                    cursor: "pointer",
                    background: isDark ? "#3a3a3a" : "#e5e7eb",
                    color: isDark ? "#f3f4f6" : "#111827"
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmKitBuilder}
                  disabled={!kitBuilder.targetIdentity}
                  style={{
                    border: "none",
                    borderRadius: 6,
                    padding: "0.45rem 0.9rem",
                    cursor: kitBuilder.targetIdentity ? "pointer" : "not-allowed",
                    background: kitBuilder.targetIdentity ? "#d97706" : "#9aa5b1",
                    color: "#fff",
                    opacity: kitBuilder.targetIdentity ? 1 : 0.7
                  }}
                >
                  Continue to Kit Editor →
                </button>
              </div>
            </div>
          </div>
        )}
        <ManageOEMModal
          open={manageOEMOpen}
          onClose={() => setManageOEMOpen(false)}
          initialOEMNumber={manageInitialOEMNumber}
          initialOEMKey={manageInitialOEMKey}
          canEdit={canWrite}
        />
        <ManageSKUModal
          open={manageSKUOpen}
          onClose={() => setManageSKUOpen(false)}
          initialSKUGroup={manageInitialSKUGroup}
          isDark={isDark}
          canEdit={canWrite}
        />
        <KittingModal
          open={kitModalState.open}
          onClose={closeKitModal}
          initialKitId={kitModalState.kitId}
          fitmentContext={kitModalState.fitment}
          initialComponents={kitModalState.initialComponents || null}
          isDark={isDark}
          canEdit={canManageKits}
          ensureFitment={ensureFitmentForKit}
          onKitLinked={handleKitLinked}
        />
        <AddOneOffModal
          open={!!oneOffContext}
          onClose={() => setOneOffContext(null)}
          baseRow={oneOffContext?.baseRow || null}
          productGroupKey={oneOffContext?.productGroupKey || null}
          subProductGroupKey={oneOffContext?.subProductGroupKey || null}
          modelKey={oneOffContext?.modelKey || null}
          existingFitmentLocationKeys={oneOffContext?.existingKeys || []}
          onAddGhostRow={addGhostOneOffRow}
          onOneOffPromoted={handleOneOffPromoted}
          canCreateItemType={canWrite}
        />
      </div>
    </div>
  );
}